import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Tabs, Tab, Box, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Checkbox, Chip, OutlinedInput, TextField, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { BarChart as BarChartIcon, TableChart as TableChartIcon, Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/mc-loading-indicator";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McMultiSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-multi-select";
import { McOption } from "@maersk-global/mds-react-wrapper/components-core/mc-option";
import { McDateRange } from "@maersk-global/mds-react-wrapper/components-core/mc-date-range";
import { API_URL } from "../../services/api-config";
// import MetricsChatbot from "./metrics-chatbot/metrics-chatbot"; // [metrics-chatbot] commented out
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Pre-defined color palettes for better performance
const STATUS_COLORS = {
  'Open': '#FFD029',
  'OPEN': '#FFD029',
  'ON-HOLD': '#E2E2E2',
  'On Hold': '#E2E2E2',
  'Rejected': '#EA5D4B',
  'REJECTED': '#EA5D4B',
  'Approved': '#42B0D5',
  'APPROVED': '#42B0D5',
  'Closed': '#1686BD',
  'CLOSED': '#1686BD',
  'Exception': '#6A6A6A',
  'EXCEPTION': '#6A6A6A',
  'COMPLIANT': '#6CA926',
  'Compliant': '#6CA926',
  'NON-COMPLIANT': '#B80012',
  'Non Compliant': '#B80012',
  'COMPLIANT-Reopened': '#97DECD',
  'Compliant-Reopened': '#97DECD',
  'EXCEPTION-Reopened': '#EA5D4B',
  'Exception-Reopened': '#EA5D4B',
  'APPROVED-Reopened': '#B9E371',
  'Approved-Reopened': '#B9E371',
  'OPEN-Reopened': '#FED195',
  'Open-Reopened': '#FED195',
  'NON-AFR': '#119C85',
  'Non-AFR': '#119C85'
};

const COUNTRY_COLOR_PALETTE = [
  '#003E62', '#1686BD', '#42B0D5', '#A1D1E8', '#E2F3FB',
  '#6A6A6A', '#D4D4D4', '#F0F0F0', '#034E45', '#119C85',
  '#97DECD', '#DFF7EE', '#316307', '#6CA926', '#B9E371',
  '#E8F6D0', '#FFF5DD', '#0077B2', '#903E00', '#806814',
  '#FFEBA8', '#40AB35', '#FFD029', '#E2E2E2', '#141414'
];

// Country flag emoji mapping
const COUNTRY_FLAG_MAP = {
  'Argentina': '🇦🇷', 'Australia': '🇦🇺', 'Austria': '🇦🇹', 'Bangladesh': '🇧🇩',
  'Belgium': '🇧🇪', 'Brazil': '🇧🇷', 'Canada': '🇨🇦', 'Chile': '🇨🇱',
  'China': '🇨🇳', 'Colombia': '🇨🇴', 'Denmark': '🇩🇰', 'Ecuador': '🇪🇨',
  'Egypt': '🇪🇬', 'Finland': '🇫🇮', 'France': '🇫🇷', 'Germany': '🇩🇪',
  'Ghana': '🇬🇭', 'Greece': '🇬🇷', 'Hong Kong': '🇭🇰', 'Hungary': '🇭🇺',
  'India': '🇮🇳', 'Indonesia': '🇮🇩', 'Ireland': '🇮🇪', 'Israel': '🇮🇱',
  'Italy': '🇮🇹', 'Japan': '🇯🇵', 'Kenya': '🇰🇪', 'Kuwait': '🇰🇼',
  'Malaysia': '🇲🇾', 'Mexico': '🇲🇽', 'Morocco': '🇲🇦', 'Netherlands': '🇳🇱',
  'New Zealand': '🇳🇿', 'Nigeria': '🇳🇬', 'Norway': '🇳🇴', 'Pakistan': '🇵🇰',
  'Peru': '🇵🇪', 'Philippines': '🇵🇭', 'Poland': '🇵🇱', 'Portugal': '🇵🇹',
  'Qatar': '🇶🇦', 'Romania': '🇷🇴', 'Russia': '🇷🇺', 'Saudi Arabia': '🇸🇦',
  'Singapore': '🇸🇬', 'South Africa': '🇿🇦', 'South Korea': '🇰🇷', 'Spain': '🇪🇸',
  'Sri Lanka': '🇱🇰', 'Sweden': '🇸🇪', 'Switzerland': '🇨🇭', 'Taiwan': '🇹🇼',
  'Thailand': '🇹🇭', 'Turkey': '🇹🇷', 'UAE': '🇦🇪', 'United Arab Emirates': '🇦🇪',
  'United Kingdom': '🇬🇧', 'UK': '🇬🇧', 'United States': '🇺🇸', 'USA': '🇺🇸',
  'Vietnam': '🇻🇳'
};

// Country-to-timezone mapping for timezone conversion
const COUNTRY_TIMEZONE_MAP = {
  'Argentina': 'America/Argentina/Buenos_Aires',
  'Australia': 'Australia/Sydney',
  'Austria': 'Europe/Vienna',
  'Bangladesh': 'Asia/Dhaka',
  'Belgium': 'Europe/Brussels',
  'Brazil': 'America/Sao_Paulo',
  'Canada': 'America/Toronto',
  'Chile': 'America/Santiago',
  'China': 'Asia/Shanghai',
  'Colombia': 'America/Bogota',
  'Croatia': 'Europe/Zagreb',
  'Czech Republic': 'Europe/Prague',
  'Denmark': 'Europe/Copenhagen',
  'Ecuador': 'America/Guayaquil',
  'Egypt': 'Africa/Cairo',
  'Finland': 'Europe/Helsinki',
  'France': 'Europe/Paris',
  'Germany': 'Europe/Berlin',
  'Ghana': 'Africa/Accra',
  'Greece': 'Europe/Athens',
  'Guatemala': 'America/Guatemala',
  'Hong Kong': 'Asia/Hong_Kong',
  'Hungary': 'Europe/Budapest',
  'India': 'Asia/Kolkata',
  'Indonesia': 'Asia/Jakarta',
  'Ireland': 'Europe/Dublin',
  'Israel': 'Asia/Jerusalem',
  'Italy': 'Europe/Rome',
  'Ivory Coast': 'Africa/Abidjan',
  'Jamaica': 'America/Jamaica',
  'Japan': 'Asia/Tokyo',
  'Kenya': 'Africa/Nairobi',
  'Kuwait': 'Asia/Kuwait',
  'Malaysia': 'Asia/Kuala_Lumpur',
  'Mexico': 'America/Mexico_City',
  'Morocco': 'Africa/Casablanca',
  'Netherlands': 'Europe/Amsterdam',
  'New Zealand': 'Pacific/Auckland',
  'Nigeria': 'Africa/Lagos',
  'Norway': 'Europe/Oslo',
  'Oman': 'Asia/Muscat',
  'Pakistan': 'Asia/Karachi',
  'Panama': 'America/Panama',
  'Peru': 'America/Lima',
  'Philippines': 'Asia/Manila',
  'Poland': 'Europe/Warsaw',
  'Portugal': 'Europe/Lisbon',
  'Qatar': 'Asia/Qatar',
  'Romania': 'Europe/Bucharest',
  'Russia': 'Europe/Moscow',
  'Saudi Arabia': 'Asia/Riyadh',
  'Senegal': 'Africa/Dakar',
  'Singapore': 'Asia/Singapore',
  'South Africa': 'Africa/Johannesburg',
  'South Korea': 'Asia/Seoul',
  'Spain': 'Europe/Madrid',
  'Sri Lanka': 'Asia/Colombo',
  'Sweden': 'Europe/Stockholm',
  'Switzerland': 'Europe/Zurich',
  'Taiwan': 'Asia/Taipei',
  'Tanzania': 'Africa/Dar_es_Salaam',
  'Thailand': 'Asia/Bangkok',
  'Trinidad and Tobago': 'America/Port_of_Spain',
  'Turkey': 'Europe/Istanbul',
  'UAE': 'Asia/Dubai',
  'United Arab Emirates': 'Asia/Dubai',
  'United Kingdom': 'Europe/London',
  'UK': 'Europe/London',
  'United States': 'America/New_York',
  'USA': 'America/New_York',
  'Uruguay': 'America/Montevideo',
  'Venezuela': 'America/Caracas',
  'Vietnam': 'Asia/Ho_Chi_Minh'
};

// Detect the user's laptop / browser timezone
const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

// Helper: convert a date string to a given IANA timezone
const convertToTimezone = (dateStr, timezone) => {
  if (!dateStr || !timezone) return dateStr;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return dateStr;
  }
};

// Utility functions moved outside component for better performance
const parseDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  return isNaN(date.getTime()) ? null : date;
};

const getWeekKey = (date) => {
  const year = date.getFullYear();
  const firstDayOfYear = new Date(year, 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  return `${year}-W${weekNumber.toString().padStart(2, '0')}`;
};

const getMonthKey = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
};

// Shared helper: parse and normalize a date string to formatted parts (day, month, year) in a given timezone
// Returns { day, mon, yr, isDateOnly } or null if parsing fails
const parseDateInTimezone = (dateValue, timezone) => {
  const raw = String(dateValue).trim();
  
  // Special case: date-only string (YYYY-MM-DD) — preserve calendar date, avoid timezone conversion
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number);
    const dateOnly = new Date(year, month - 1, day);
    if (isNaN(dateOnly.getTime())) return null;
    const mon = dateOnly.toLocaleString('en-US', { month: 'short' });
    const yr = String(year).slice(-2);
    return {
      day: String(day).padStart(2, '0'),
      mon,
      yr,
      isDateOnly: true
    };
  }
  
  // General case: parse ISO-like or space-separated datetime
  let utcStr = raw.includes('T') ? raw : raw.replace(' ', 'T');
  if (!/(?:[zZ]|[+-]\d{2}:\d{2})$/.test(utcStr)) {
    utcStr += 'Z'; // assume UTC if no offset
  }
  
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return null;
  
  const day = d.toLocaleString('en-US', { timeZone: timezone, day: '2-digit' });
  const mon = d.toLocaleString('en-US', { timeZone: timezone, month: 'short' });
  const yr = d.toLocaleString('en-US', { timeZone: timezone, year: '2-digit' });
  
  return { day, mon, yr, isDateOnly: false, dateObj: d };
};

// Utility function: format a date string to DD-MMM-YY h:MM:SS AM/PM (12-hour format) in a given timezone
const formatDateTimeWithTimezone = (dateValue, timezone) => {
  if (!dateValue) return '';
  try {
    const parsed = parseDateInTimezone(dateValue, timezone);
    if (!parsed) return dateValue;
    
    const { day, mon, yr, dateObj } = parsed;
    
    // If date-only, skip time formatting
    if (parsed.isDateOnly) {
      return `${day}-${mon}-${yr}`;
    }
    
    const timeStr = dateObj.toLocaleString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    
    return `${day}-${mon}-${yr} ${timeStr}`;
  } catch {
    return dateValue;
  }
};

const formatCommaSeparatedDates = (value, timezone) => {
  const parts = String(value).split(',').map(v => v.trim()).filter(Boolean);
  return parts.map(p => formatDateTimeWithTimezone(p, timezone)).join(', ');
};

const formatYesNoValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  const normalizedValue = String(value).trim().toLowerCase();

  if (['true', 'yes', 'y', '1'].includes(normalizedValue)) {
    return 'Yes';
  }

  if (['false', 'no', 'n', '0'].includes(normalizedValue)) {
    return 'No';
  }

  return String(value).trim() || 'N/A';
};

// Utility function: format a date string to DD-MMM-YY (date only, no time) in a given timezone
const formatDateOnly = (dateValue, timezone) => {
  if (!dateValue) return '';
  try {
    const parsed = parseDateInTimezone(dateValue, timezone);
    if (!parsed) return dateValue;
    
    const { day, mon, yr } = parsed;
    return `${day}-${mon}-${yr}`;
  } catch {
    return dateValue;
  }
};

const formatCommaSeparatedDatesOnly = (value, timezone) => {
  const parts = String(value).split(',').map(v => v.trim()).filter(Boolean);
  return parts.map(p => formatDateOnly(p, timezone)).join(', ');
};

// Helper function to get consistent status color regardless of case
// This ensures colors match between chart and table for all status variations
const getStatusColor = (status) => {
  if (!status) return '#6A6A6A'; // Default fallback color
  
  const statusStr = String(status).trim();
  
  // First, try exact match in STATUS_COLORS
  if (STATUS_COLORS[statusStr]) {
    return STATUS_COLORS[statusStr];
  }
  
  // If no exact match, try to find by normalized comparison
  // This handles case variations like "Approved" vs "APPROVED" vs "approved"
  const statusLower = statusStr.toLowerCase();
  const statusHyphenated = statusStr.toLowerCase().replace(/\s+/g, '-');
  
  for (const [key, color] of Object.entries(STATUS_COLORS)) {
    const keyLower = key.toLowerCase();
    const keyHyphenated = key.toLowerCase().replace(/\s+/g, '-');
    
    // Match by lowercase comparison
    if (keyLower === statusLower || keyHyphenated === statusHyphenated) {
      return color;
    }
    
    // Match by removing spaces/hyphens and comparing
    const keyNormalized = key.toLowerCase().replace(/[\s\-]/g, '');
    const statusNormalized = statusStr.toLowerCase().replace(/[\s\-]/g, '');
    if (keyNormalized === statusNormalized) {
      return color;
    }
  }
  
  // Fallback to default color if no match found
  return '#6A6A6A';
};

const MetricsSnapshot = () => {
  // ── Core aggregated state (from new lightweight APIs) ──────────────────
  const [summary, setSummary] = useState(null);           // scalar totals from /metrics/summary
  const [countryAgg, setCountryAgg] = useState([]);       // country breakdown from /metrics/aggregates?group_by=country
  const [statusAgg, setStatusAgg] = useState([]);         // status breakdown from /metrics/aggregates?group_by=status
  const [trendsData, setTrendsData] = useState(null);     // null = not loaded yet, [] = loaded but empty

  // ── UI / interaction state ─────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedDetailedSummaryYear, setSelectedDetailedSummaryYear] = useState(new Date().getFullYear());
  const [showDetailedTable, setShowDetailedTable] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for Trends, 1 for Detailed Summary, 2 for Productivity Report
  const [selectedTrendsCountry, setSelectedTrendsCountry] = useState([]);
  const countryMultiSelectRef = useRef(null);
  const [filteredCountryData, setFilteredCountryData] = useState([]);
  const [selectedCountryForDetails, setSelectedCountryForDetails] = useState(null);
  const [loadingCountryData, setLoadingCountryData] = useState(false);
  const [selectedCaseStatus, setSelectedCaseStatus] = useState(null);
  const [filteredStatusData, setFilteredStatusData] = useState([]);
  const [showStatusDetailedTable, setShowStatusDetailedTable] = useState(false);
  const [showCaseStatusDistribution, setShowCaseStatusDistribution] = useState(false);
  const [detailedTableSearchText, setDetailedTableSearchText] = useState('');
  const [statusTableSearchText, setStatusTableSearchText] = useState('');
  const [countryDistributionView, setCountryDistributionView] = useState('chart');
  const [trendsTimePeriod, setTrendsTimePeriod] = useState('weekly');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Helper: yesterday's date string for Productivity Report default
  // Uses local date parts to avoid UTC conversion issues across timezones
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  // Helper: 1 month ago — min selectable "From" date for Productivity Report
  const oneMonthAgo = useMemo(() => {
    const d = new Date();
    const dayOfMonth = d.getDate();
    d.setDate(1); // prevent month rollover issues
    d.setMonth(d.getMonth() - 1);
    const lastDayOfTargetMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  // Helper: 10 days ago — min selectable "From" date for Detailed Summary tab
  const tenDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 10);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [dateRangeKey, setDateRangeKey] = useState(0);
  const [selectedTimezone, setSelectedTimezone] = useState(LOCAL_TIMEZONE);
  const [selectedPurpose, setSelectedPurpose] = useState('All');
  const [executiveSummaryDays, setExecutiveSummaryDays] = useState(30);
  const [executiveSummaryData, setExecutiveSummaryData] = useState([]);
  const [loadingExecutiveSummary, setLoadingExecutiveSummary] = useState(false);

  // ── Drill-down detail table state (server-paginated) ───────────────────
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(50);
  const [statusDetailRows, setStatusDetailRows] = useState([]);
  const [statusDetailTotal, setStatusDetailTotal] = useState(0);
  const [statusDetailPage, setStatusDetailPage] = useState(0);
  const [statusDetailPageSize, setStatusDetailPageSize] = useState(50);

  // ── Country-status breakdown for Detailed Summary tab ──────────────────
  const [countryStatusAgg, setCountryStatusAgg] = useState([]);
  const [loadingCountryStatusAgg, setLoadingCountryStatusAgg] = useState(false);
  // Trends drilldown modal state
  const [trendsDrilldownOpen, setTrendsDrilldownOpen] = useState(false);
  const [trendsDrilldownStatus, setTrendsDrilldownStatus] = useState(null);
  const [trendsDrilldownWeekLabel, setTrendsDrilldownWeekLabel] = useState('');
  const [trendsDrilldownDailyData, setTrendsDrilldownDailyData] = useState({});

  // ── Productivity Report tab state ──────────────────────────────────────
  const [productivityData, setProductivityData] = useState([]);
  const [loadingProductivityData, setLoadingProductivityData] = useState(false);
  const [productivityTableSearchText, setProductivityTableSearchText] = useState('');
  // Server-side pagination state for Productivity Report
  const [productivityPage, setProductivityPage] = useState(0);
  const [productivityPageSize, setProductivityPageSize] = useState(50);
  const [productivityTotal, setProductivityTotal] = useState(0);
  const [productivitySortModel, setProductivitySortModel] = useState([{ field: 'Case Creation Week', sort: 'desc' }]);
  // Export state
  const [exportingProductivity, setExportingProductivity] = useState(false);

  // ══════════════════════════════════════════════════════════════════════
  // 1. INITIAL LOAD: summary + country aggregates (3 small API calls)
  // ══════════════════════════════════════════════════════════════════════
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('start_date', dateRange.from);
      if (dateRange.to) params.append('end_date', dateRange.to);

      // Always send date range to summary endpoint
      const summaryUrl = `${API_URL}/metrics/summary?${params}`;
      const countryUrl = `${API_URL}/metrics/aggregates?group_by=country&${params}`;
      const statusUrl = `${API_URL}/metrics/aggregates?group_by=status&${params}`;

      const [summaryRes, countryRes, statusRes] = await Promise.all([
        fetch(summaryUrl),
        fetch(countryUrl),
        fetch(statusUrl),
      ]);

      if (summaryRes.ok) {
        try { setSummary(await summaryRes.json()); } catch (_) { /* non-JSON response */ }
      }
      if (countryRes.ok) {
        try { setCountryAgg(await countryRes.json()); } catch (_) { /* non-JSON response */ }
      }
      if (statusRes.ok) {
        try { setStatusAgg(await statusRes.json()); } catch (_) { /* non-JSON response */ }
      }

      setTimeout(() => setIsInitialLoad(false), 100);
    } catch (err) {
      console.error('Failed to load metrics summary:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // ══════════════════════════════════════════════════════════════════════
  // 2. TRENDS: load when Trends tab is active + countries selected
  // ══════════════════════════════════════════════════════════════════════
  const fetchTrends = useCallback(async () => {
    if (selectedTrendsCountry.length === 0) return;
    // Reset trendsData so summaryMetrics shows 0 (loading) instead of stale values
    // from a previous date range / country selection while the new fetch is in-flight.
    setTrendsData(null);
    try {
      const params = new URLSearchParams();
      params.append('countries', selectedTrendsCountry.join(','));
      params.append('period', trendsTimePeriod);
      // When a custom date range is applied, send it instead of the default days window
      if (dateRange.from || dateRange.to) {
        if (dateRange.from) params.append('start_date', dateRange.from);
        if (dateRange.to) params.append('end_date', dateRange.to);
      } else {
        params.append('days', String(executiveSummaryDays));
      }

      const res = await fetch(`${API_URL}/metrics/trends?${params}`);
      if (res.ok) setTrendsData(await res.json());
    } catch (err) {
      console.error('Failed to load trends:', err);
    }
  }, [selectedTrendsCountry, trendsTimePeriod, executiveSummaryDays, dateRange]);

  useEffect(() => {
    if (activeTab === 0) fetchTrends();
  }, [activeTab, fetchTrends]);

  // ══════════════════════════════════════════════════════════════════════
  // 3. EXECUTIVE SUMMARY: from dedicated API
  // ══════════════════════════════════════════════════════════════════════
  const fetchExecutiveSummary = useCallback(async () => {
    if (selectedTrendsCountry.length === 0) {
      setLoadingExecutiveSummary(false);
      setExecutiveSummaryData([]);
      return;
    }
    setLoadingExecutiveSummary(true);
    try {
      const params = new URLSearchParams();
      params.append('countries', selectedTrendsCountry.join(','));
      // When a custom date range is applied, send it instead of the default days window
      if (dateRange.from || dateRange.to) {
        if (dateRange.from) params.append('start_date', dateRange.from);
        if (dateRange.to) params.append('end_date', dateRange.to);
      } else {
        params.append('days', String(executiveSummaryDays));
      }

      console.log(`[Executive Summary] Fetching: ${API_URL}/metrics/executive-summary?${params}`);
      const res = await fetch(`${API_URL}/metrics/executive-summary?${params}`);
      if (res.ok) {
        const data = await res.json();
        console.log(`[Executive Summary] Received ${data.length} rows`);
        setExecutiveSummaryData(data);
      } else {
        const errorText = await res.text();
        console.error(`[Executive Summary] HTTP ${res.status}: ${errorText}`);
        setExecutiveSummaryData([]);
      }
    } catch (err) {
      console.error('[Executive Summary] Network error:', err);
      setExecutiveSummaryData([]);
    } finally {
      setLoadingExecutiveSummary(false);
    }
  }, [selectedTrendsCountry, executiveSummaryDays, dateRange]);

  useEffect(() => {
    if (activeTab === 0) fetchExecutiveSummary();
  }, [activeTab, fetchExecutiveSummary]);

  // ══════════════════════════════════════════════════════════════════════
  // 4. DRILL-DOWN DETAILS: paginated raw rows (on demand only)
  // ══════════════════════════════════════════════════════════════════════
  const fetchDetailRows = useCallback(async (filters = {}) => {
    setLoadingCountryData(true);
    try {
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.casestatus) params.append('casestatus', filters.casestatus);
      if (filters.year) params.append('year', filters.year);
      if (dateRange.from) params.append('start_date', dateRange.from);
      if (dateRange.to) params.append('end_date', dateRange.to);

      const res = await fetch(`${API_URL}/get_metrics_snapshot?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];

      // Robust frontend filtering for timezone-aware date range
      let filteredRows = rows;
      if (dateRange.from && dateRange.to && selectedTimezone) {
        // Build the selected range as plain YYYY-MM-DD values so a single-day
        // selection stays on the same calendar date in every timezone.
        const validDates = new Set();
        let current = new Date(`${dateRange.from}T00:00:00Z`);
        const end = new Date(`${dateRange.to}T00:00:00Z`);
        while (current <= end) {
          validDates.add(current.toISOString().slice(0, 10));
          current.setUTCDate(current.getUTCDate() + 1);
        }

        // Reuse parseDateInTimezone for consistent datetime parsing (handles space-separated
        // datetimes, lowercase 'z', and date-only values the same way rendering/export does).
        const normalizeRowDate = (value) => {
          const raw = String(value).trim();
          if (!raw) return null;
          const parsed = parseDateInTimezone(raw, selectedTimezone);
          if (!parsed) return null;
          if (parsed.isDateOnly) {
            // Date-only: reconstruct YYYY-MM-DD from the parsed parts
            const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(parsed.mon);
            if (monthIndex === -1) return null;
            const year = Number(`20${parsed.yr}`);
            const month = String(monthIndex + 1).padStart(2, '0');
            return `${year}-${month}-${parsed.day}`;
          }
          // Datetime: convert to YYYY-MM-DD in the selected timezone
          return parsed.dateObj.toLocaleDateString('en-CA', { timeZone: selectedTimezone });
        };

        filteredRows = rows.filter(row => {
          const raw = row['Date of the Request/Case'] || row['date of the request/case'];
          if (!raw) return false;
          // Support comma-separated dates (shouldn't happen, but robust)
          const dateParts = String(raw).split(',').map(s => s.trim()).filter(Boolean);
          // If any date part matches, include the row
          return dateParts.some(dateStr => {
            const normalizedDate = normalizeRowDate(dateStr);
            return normalizedDate ? validDates.has(normalizedDate) : false;
          });
        });
      }

      setDetailRows(filteredRows);
      setDetailTotal(filteredRows.length);
      setFilteredCountryData(filteredRows);
    } catch (err) {
      console.error('Failed to load detail rows:', err);
    } finally {
      setLoadingCountryData(false);
    }
  }, [dateRange, selectedTimezone]);

  // ══════════════════════════════════════════════════════════════════════
  // 6. PRODUCTIVITY REPORT: fetch and expand BL numbers
  // ══════════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════════
  // 6. PRODUCTIVITY REPORT: server-side paginated fetch from vw_afr_productivity_reporting_v2
  //    - Queries afr_db.vw_afr_productivity_reporting_v2 view
  //    - Returns 14 columns: case week, user, status, dates, BL, SLA, etc.
  //    - Row expansion: comma-separated BL numbers → separate rows
  //    - Server-side pagination: single API call with limit/offset, next page on user action
  // ══════════════════════════════════════════════════════════════════════
  const fetchProductivityReport = useCallback(async (page = productivityPage, pageSize = productivityPageSize, countries = selectedTrendsCountry) => {
    setLoadingProductivityData(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('start_date', dateRange.from);
      if (dateRange.to) {
        // Add one day to end_date so the backend's < filter includes the selected day
        // Use local date arithmetic to avoid UTC conversion issues across timezones
        const [y, m, d] = dateRange.to.split('-').map(Number);
        const endDate = new Date(y, m - 1, d + 1);
        const ey = endDate.getFullYear();
        const em = String(endDate.getMonth() + 1).padStart(2, '0');
        const ed = String(endDate.getDate()).padStart(2, '0');
        params.append('end_date', `${ey}-${em}-${ed}`);
      }
      if (countries.length > 0) {
        params.append('countries', countries.join(','));
      }
      // Server-side pagination — single call
      params.append('limit', String(pageSize));
      params.append('offset', String(page * pageSize));

      // Server-side sorting
      if (productivitySortModel.length > 0) {
        params.append('sort_by', productivitySortModel[0].field);
        params.append('sort_order', productivitySortModel[0].sort);
      }

      const res = await fetch(`${API_URL}/metrics/productivity-report?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Handle paginated response format {items, total, limit, offset}
      const rows = data.items && Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = data.total != null ? data.total : rows.length;

      // Assign stable IDs without expanding comma-separated values.
      // Expansion is intentionally skipped here to preserve server-side pagination
      // semantics (pageSize and rowCount stay consistent). Expansion is only applied
      // during full-data export where all rows are fetched without pagination.
      const displayRows = rows.map((row, idx) => ({
        ...row,
        id: `${page}-${idx}`
      }));

      setProductivityData(displayRows);
      setProductivityTotal(total);

      // Auto-set timezone based on country in productivity data
      if (displayRows.length > 0 && countries.length === 0) {
        const countrySet = new Set();
        displayRows.forEach(row => {
          const country = row['Country'];
          if (country) countrySet.add(country);
        });
        if (countrySet.size === 1) {
          const countryName = Array.from(countrySet)[0];
          const countryTimezone = COUNTRY_TIMEZONE_MAP[countryName];
          if (countryTimezone) {
            setSelectedTimezone(countryTimezone);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load productivity report:', err);
      setProductivityData([]);
      setProductivityTotal(0);
    } finally {
      setLoadingProductivityData(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, productivityPage, productivityPageSize, productivitySortModel]);

  // Single effect: fetch when tab is active AND page/dateRange/sort/country changes
  const prevCountriesRef = useRef(selectedTrendsCountry);
  useEffect(() => {
    if (activeTab !== 2) return;

    // Reset to page 0 when countries change
    const countriesChanged = JSON.stringify(prevCountriesRef.current) !== JSON.stringify(selectedTrendsCountry);
    prevCountriesRef.current = selectedTrendsCountry;

    if (countriesChanged && productivityPage !== 0) {
      setProductivityPage(0); // This will re-trigger this effect with page=0
      return;
    }

    // Country is a mandatory filter — clear data when no countries selected
    if (selectedTrendsCountry.length === 0) {
      setProductivityData([]);
      setProductivityTotal(0);
      return;
    }

    fetchProductivityReport(productivityPage, productivityPageSize, selectedTrendsCountry);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, productivityPage, productivityPageSize, dateRange, productivitySortModel, selectedTrendsCountry]);

  // Update timezone based on selected countries in Productivity Report tab
  useEffect(() => {
    if (activeTab !== 2) return; // Only apply to Productivity Report tab
    if (selectedTrendsCountry.length === 0) return; // Exit if no countries selected

    // If single country selected, use its timezone
    if (selectedTrendsCountry.length === 1) {
      const countryName = selectedTrendsCountry[0];
      const countryTimezone = COUNTRY_TIMEZONE_MAP[countryName];
      if (countryTimezone) {
        setSelectedTimezone(countryTimezone);
      }
    } else if (selectedTrendsCountry.length > 1) {
      // If multiple countries selected, use the first one's timezone
      const firstCountry = selectedTrendsCountry[0];
      const countryTimezone = COUNTRY_TIMEZONE_MAP[firstCountry];
      if (countryTimezone) {
        setSelectedTimezone(countryTimezone);
      }
    }
  }, [activeTab, selectedTrendsCountry]);

  useEffect(() => {
    if (activeTab !== 1) return;
    if (showDetailedTable && selectedCountryForDetails) {
      fetchDetailRows({ country: selectedCountryForDetails });
    }
  }, [
    activeTab,
    dateRange.from,
    dateRange.to,
    fetchDetailRows,
    selectedCountryForDetails,
    showDetailedTable,
  ]);

  // ══════════════════════════════════════════════════════════════════════
  // 5. Derived data from aggregated API responses (no raw-row scanning)
  // ══════════════════════════════════════════════════════════════════════

  // Available countries from aggregate data
  const availableCountries = useMemo(() => {
    return countryAgg.map(item => item.country).filter(Boolean).sort();
  }, [countryAgg]);

  // Country colors
  const countryColors = useMemo(() => {
    const colors = {};
    availableCountries.forEach((country, index) => {
      if (index < COUNTRY_COLOR_PALETTE.length) {
        colors[country] = COUNTRY_COLOR_PALETTE[index];
      } else {
        const hue = (index * 137.5) % 360;
        colors[country] = `hsl(${hue}, 60%, 50%)`;
      }
    });
    return colors;
  }, [availableCountries]);

  // Chart data for country distribution bar chart — directly from aggregate API
  const chartData = useMemo(() => {
    return countryAgg
      .map((item, index) => ({
        country: item.country,
        totalCases: item.total_cases,
        fill: COUNTRY_COLOR_PALETTE[index % COUNTRY_COLOR_PALETTE.length]
      }))
      .sort((a, b) => b.totalCases - a.totalCases);
  }, [countryAgg]);

  // Filtered productivity data — server handles country/date filtering via pagination
  // No client-side expansion or deduplication needed since rows are displayed as-is from server
  const filteredProductivityData = useMemo(() => {
    if (!productivityData || productivityData.length === 0) {
      return [];
    }
    return productivityData;
  }, [productivityData]);

  // Total cases count — use server-provided total for accurate pagination count
  const productivityTotalCases = useMemo(() => {
    return productivityTotal;
  }, [productivityTotal]);

  // Summary metrics — always reflect selected date range
  const summaryMetrics = useMemo(() => {
    if (!summary) return { totalCases: 0, totalCountries: 0, totalStatuses: 0, totalClusters: 0 };

    // Always use filtered data for selected date range
    const globalStatuses = statusAgg.length || summary.total_statuses || 0;

    // When countries are selected, compute totals consistent with trends data
    if (selectedTrendsCountry.length > 0 && countryAgg.length > 0) {
      const selectedSet = new Set(selectedTrendsCountry);
      const filteredCountries = countryAgg.filter(item => selectedSet.has(item.country));

      // Use trendsData (filtered by dateRange) for consistent counts
      let totalCases = 0;
      let totalStatuses = 0;

      if (trendsData !== null) {
        // Only count cases within the selected date range
        const uniqueStatuses = new Set();
        trendsData.forEach(item => {
          if (selectedSet.has(item.country)) {
            totalCases += (item.count || 0);
            if (item.status) uniqueStatuses.add(item.status);
          }
        });
        totalStatuses = uniqueStatuses.size;
      } else {
        totalCases = 0;
        totalStatuses = 0;
      }

      return {
        totalCases,
        totalCountries: filteredCountries.length,
        totalStatuses,
        totalClusters: summary.total_clusters || 0
      };
    }

    // No country filter — show totals for selected date range only
    return {
      totalCases: summary.total_cases || 0,
      totalCountries: summary.total_countries || 0,
      totalStatuses: globalStatuses,
      totalClusters: summary.total_clusters || 0
    };
  }, [summary, selectedTrendsCountry, countryAgg, trendsData, statusAgg, dateRange]);

  // Case status distribution for selected country (Detailed Summary tab)
  const yearWiseChartData = useMemo(() => {
    if (!selectedCountry || countryStatusAgg.length === 0) return [];
    return countryStatusAgg
      .filter(item => item.country === selectedCountry)
      .map(item => ({ status: item.status, count: item.total_cases }))
      .sort((a, b) => b.count - a.count);
  }, [selectedCountry, countryStatusAgg]);

  // Set default country when data loads, or fix up stale selection after date-range change
  useEffect(() => {
    if (availableCountries.length === 0) return;
    // On Productivity Report tab, if no country selected then display message that select the country filter for productivity report tab
    if (activeTab === 2 && selectedTrendsCountry.length === 0) return;
    if (selectedTrendsCountry.length === 0) {
      // No selection yet — pick Malaysia if available, else first available country
      if (availableCountries.includes('Malaysia')) {
        setSelectedTrendsCountry(['Malaysia']);
      } else {
        setSelectedTrendsCountry([availableCountries[0]]);
      }
    } else {
      // Ensure every selected country still exists in the new list
      const validSet = new Set(availableCountries);
      const stillValid = selectedTrendsCountry.filter(c => validSet.has(c));
      if (stillValid.length < selectedTrendsCountry.length) {
        // Some countries dropped out (date range excluded them) — keep what's still valid
        if (availableCountries.includes('Malaysia')) {
          setSelectedTrendsCountry(['Malaysia']);
        } else {
          setSelectedTrendsCountry(stillValid.length > 0 ? stillValid : [availableCountries[0]]);
        }
      }
    }
  }, [availableCountries, selectedTrendsCountry, activeTab]);

  // Ensure metrics update when either country or date changes
  useEffect(() => {
    // Skip on Productivity Report tab — it has its own dedicated fetch logic
    if (activeTab === 2) return;
    // Only update if both country and date are selected
    if (selectedTrendsCountry.length > 0 && dateRange.from && dateRange.to) {
      fetchInitialData(); // This will update summary, countryAgg, statusAgg
      fetchTrends();
      fetchExecutiveSummary();
    }
  }, [selectedTrendsCountry, dateRange.from, dateRange.to, activeTab]);

  // Dashboard card metrics — built from trendsData (server-aggregated)
  const dashboardMetrics = useMemo(() => {
    if (selectedTrendsCountry.length === 0 || !trendsData || trendsData.length === 0) return {};

    const selectedSet = new Set(selectedTrendsCountry);

    // Aggregate status counts across selected countries
    const statusCounts = {};
    const weekStatusMap = {}; // { weekKey: { status: count } }
    const weekKeys = new Set();

    trendsData.forEach(item => {
      if (!selectedSet.has(item.country)) return;
      const status = item.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + item.count;

      const wk = item.period;
      weekKeys.add(wk);
      if (!weekStatusMap[wk]) weekStatusMap[wk] = {};
      weekStatusMap[wk][status] = (weekStatusMap[wk][status] || 0) + item.count;
    });

    const sortedWeeks = Array.from(weekKeys).sort();

    const getQuickStatusConfig = (status) => {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('approved') || (statusLower.includes('compliant') && !statusLower.includes('non'))) {
        return { icon: '✅', color: '#6CA926' };
      } else if (statusLower.includes('rejected') || statusLower.includes('non-compliant') || statusLower.includes('non compliant')) {
        return { icon: '❌', color: '#FDA99E' };
      } else if (statusLower.includes('pending') || statusLower.includes('on-hold')) {
        return { icon: '⏳', color: '#FED195' };
      } else if (statusLower.includes('exception')) {
        return { icon: '⚠️', color: '#F3870C' };
      }
      return { icon: '📋', color: '#A1D1E8' };
    };

    const formatValue = (count) => count >= 1000 ? `${(count / 1000).toFixed(1)}K` : count.toString();

    const metrics = {};
    Object.entries(statusCounts).forEach(([status, count]) => {
      const config = getQuickStatusConfig(status);
      const key = status.toLowerCase().replace(/[^a-z0-9]/g, '');
      const trendArr = sortedWeeks.map(wk => ({
        week: wk,
        value: (weekStatusMap[wk] && weekStatusMap[wk][status]) || 0
      }));

      metrics[key] = {
        title: `${status} Cases`,
        value: formatValue(count),
        trend: trendArr,
        unit: 'Cases',
        icon: config.icon,
        color: config.color,
        originalStatus: status
      };
    });

    return metrics;
  }, [selectedTrendsCountry, trendsData]);

  // Country distribution table data
  const countryDistributionTableData = useMemo(() => {
    if (!chartData.length) return [];
    const total = summaryMetrics.totalCases || 1;
    return chartData.map((item, index) => ({
      id: index + 1,
      country: item.country,
      totalCases: item.totalCases,
      percentage: ((item.totalCases / total) * 100).toFixed(2)
    }));
  }, [chartData, summaryMetrics]);

  // Unique statuses
  const uniqueStatuses = useMemo(() => {
    return statusAgg.map(item => item.status).filter(Boolean);
  }, [statusAgg]);

  // Optimized event handlers with useCallback
  const handleCountryClick = useCallback((data) => {
    if (data && data.activeLabel) {
      const country = data.activeLabel;
      setSelectedCountry(country);
      setSelectedCaseStatus(null);
      setShowStatusDetailedTable(false);
      setShowCaseStatusDistribution(true);
      setFilteredStatusData([]);
      setStatusTableSearchText('');
      setSelectedCountryForDetails(country);
      setShowDetailedTable(true);
      setDetailedTableSearchText('');
      setDetailPage(0);
      // Auto-set timezone to the selected country's timezone
      const countryTz = COUNTRY_TIMEZONE_MAP[country];
      if (countryTz) {
        setSelectedTimezone(countryTz);
      }
      
      // Clear old data IMMEDIATELY to prevent stale data from displaying while new fetch is in progress
      setCountryStatusAgg([]);
      setLoadingCountryStatusAgg(true);
      const params = new URLSearchParams();
      params.append('group_by', 'country_status');
      params.append('country', country);
      if (dateRange.from) params.append('start_date', dateRange.from);
      if (dateRange.to) params.append('end_date', dateRange.to);
      fetch(`${API_URL}/metrics/aggregates?${params}`)
        .then(res => res.json())
        .then(aggData => {
          setCountryStatusAgg(aggData);
          setLoadingCountryStatusAgg(false);
        })
        .catch(() => setLoadingCountryStatusAgg(false));
      fetchDetailRows({ country });
    }
  }, [dateRange, fetchDetailRows]);

  const handleYearClick = useCallback((year) => {
    try {
      console.log('Year clicked:', year);
      setSelectedYear(year);
      setShowDetailedTable(true);
    } catch (error) {
      console.error('Error in handleYearClick:', error);
    }
  }, []);

  const handleTotalCasesClick = useCallback((country) => {
    try {
      console.log('Total cases clicked for country:', country);
      setSelectedCountryForDetails(country);
      setShowDetailedTable(true);
      setDetailPage(0);
      fetchDetailRows({ country });
    } catch (error) {
      console.error('Error in handleTotalCasesClick:', error);
    }
  }, [fetchDetailRows]);

  // Optimized event handlers with useCallback
  const handleBackToSummary = useCallback(() => {
    setSelectedYear(null);
    setShowDetailedTable(false);
    setSelectedCountryForDetails(null);
    setFilteredCountryData([]);
    setDetailedTableSearchText('');
    setDetailRows([]);
    setDetailTotal(0);
  }, []);

  const handleCaseStatusClick = useCallback((data) => {
    if (data && data.activeLabel && selectedCountry) {
      const clickedStatus = data.activeLabel;
      console.log('Case status clicked:', clickedStatus, 'for country:', selectedCountry);
      setSelectedCaseStatus(clickedStatus);
      setShowStatusDetailedTable(true);
      setShowDetailedTable(false);
      setStatusDetailPage(0);
      // Auto-set timezone
      const countryTz = COUNTRY_TIMEZONE_MAP[selectedCountry];
      if (countryTz) {
        setSelectedTimezone(countryTz);
      }
      
      // Filter existing country data by the selected case status instead of making a new API call
      // This is more efficient since we already have all cases for the country
      const filteredData = filteredCountryData.filter(row => 
        row.casestatus && row.casestatus.toString().trim().toLowerCase() === clickedStatus.toLowerCase()
      );
      setStatusDetailRows(filteredData);
      setFilteredStatusData(filteredData);
      setStatusDetailTotal(filteredData.length);
    }
  }, [selectedCountry, filteredCountryData]);

  const handleBackFromStatusDetails = useCallback(() => {
    setSelectedCaseStatus(null);
    setShowStatusDetailedTable(false);
    setFilteredStatusData([]);
    setStatusTableSearchText('');
    setStatusDetailRows([]);
    setStatusDetailTotal(0);
    // Re-show country-filtered table
    if (selectedCountry) {
      setSelectedCountryForDetails(selectedCountry);
      setShowDetailedTable(true);
      setDetailPage(0);
      fetchDetailRows({ country: selectedCountry });
    }
  }, [selectedCountry, fetchDetailRows]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    // Reset selections when switching tabs
    setSelectedCountry(null);
    setSelectedYear(null);
    setSelectedDetailedSummaryYear(new Date().getFullYear());
    setShowDetailedTable(false);
    setSelectedCountryForDetails(null);
    setFilteredCountryData([]);
    setSelectedCaseStatus(null);
    setShowStatusDetailedTable(false);
    setFilteredStatusData([]);
    setCountryDistributionView('chart');
    setTrendsTimePeriod('weekly');
    setProductivityTableSearchText('');
    // Default to Turkey and yesterday's date for Productivity Report tab
    if (newValue === 2) {
      if (availableCountries.includes('Turkey')) {
        setSelectedTrendsCountry(['Turkey']);
      }
      setFilterFromDate(yesterday);
      setFilterToDate(yesterday);
      setDateRange({ from: yesterday, to: yesterday });
      setDateRangeKey((k) => k + 1);
    } else {
      // Clear date range when leaving Productivity Report tab
      setFilterFromDate('');
      setFilterToDate('');
      setDateRange({ from: null, to: null });
      setDateRangeKey((k) => k + 1);
    }
  }, [availableCountries, yesterday]);

  // Refs to hold the latest state so event handler never reads stale closure
  const selectedTrendsCountryRef = useRef(selectedTrendsCountry);
  useEffect(() => { selectedTrendsCountryRef.current = selectedTrendsCountry; }, [selectedTrendsCountry]);
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // Attach event listeners to McMultiSelect whenever the active tab changes.
  // McMultiSelect is conditionally rendered (hidden on tab 1), so it unmounts
  // and remounts when switching tabs. Re-running on activeTab ensures listeners
  // are always attached to the current DOM instance.
  useEffect(() => {
    // McMultiSelect is not rendered on tab 1 — nothing to attach
    if (activeTab === 1) return;

    let cleanupListeners = null;
    const timer = setTimeout(() => {
      const multiSelect = countryMultiSelectRef.current;
      if (multiSelect) {
        const handleChange = (e) => {
          let selectedValues = [];
          if (e.target && e.target.value) {
            selectedValues = Array.isArray(e.target.value)
              ? e.target.value
              : (typeof e.target.value === 'string' ? e.target.value.split(',').filter(Boolean) : []);
          } else if (typeof e.detail === 'string') {
            selectedValues = e.detail.split(',').filter(Boolean);
          } else if (Array.isArray(e.detail)) {
            selectedValues = e.detail.map(item => typeof item === 'string' ? item : item.value).filter(Boolean);
          }

          // Guard: when the web component fires an event with empty value during
          // a re-render (e.g. options changed), ignore it — preserve current selection.
          // Exception: on Productivity Report tab (tab 2), allow clearing all countries.
          if (selectedValues.length === 0 && selectedTrendsCountryRef.current.length > 0 && activeTabRef.current !== 2) return;

          setSelectedTrendsCountry(selectedValues);
        };

        const eventNames = ['optionselected', 'mcOptionSelected', 'input', 'change', 'valueChange'];
        eventNames.forEach(eventName => {
          multiSelect.addEventListener(eventName, handleChange);
        });

        cleanupListeners = () => {
          eventNames.forEach(eventName => {
            multiSelect.removeEventListener(eventName, handleChange);
          });
        };
      }
    }, 100);
    return () => {
      clearTimeout(timer);
      if (cleanupListeners) cleanupListeners();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Imperatively re-sync the McMultiSelect value when options re-render
  // (e.g. after Apply date range triggers new countryAgg).
  // Web components don't reliably reflect React prop changes during slot re-renders.
  useEffect(() => {
    const multiSelect = countryMultiSelectRef.current;
    if (!multiSelect) return;
    // Allow the web component time to process its new McOption children first
    const syncTimer = setTimeout(() => {
      const valueStr = selectedTrendsCountry.join(',');
      try { multiSelect.value = valueStr; } catch (_) { /* ignore */ }
      // Also mark individual options as selected for visual consistency
      try {
        const options = multiSelect.querySelectorAll('mc-option');
        const selectedSet = new Set(selectedTrendsCountry);
        options.forEach(opt => {
          const val = opt.getAttribute('value');
          if (selectedSet.has(val)) {
            opt.setAttribute('selected', '');
          } else {
            opt.removeAttribute('selected');
          }
        });
      } catch (_) { /* ignore */ }
    }, 150);
    return () => clearTimeout(syncTimer);
  }, [availableCountries, selectedTrendsCountry]);

  const handleDetailedSummaryYearChange = useCallback((event) => {
    setSelectedDetailedSummaryYear(event.target.value);
  }, []);

  // Date range handler for McDateRange
  // Store dates locally — do NOT trigger re-fetch until Apply is clicked
  const handleDateRangeChange = useCallback((e) => {
    const value = e.detail?.value || e.target?.value;
    if (value) {
      setFilterFromDate(value.from || '');
      setFilterToDate(value.to || '');
    }
  }, []);

  // Apply: push local dates into dateRange → triggers API re-fetch
  const handleApplyFilters = useCallback(() => {
    setDateRange({
      from: filterFromDate || null,
      to: filterToDate || null,
    });
    if (filterFromDate && filterToDate) {
      setSelectedCaseStatus(null);
      setShowStatusDetailedTable(false);
      setShowCaseStatusDistribution(false);
      setShowDetailedTable(false);
      setSelectedCountry(null);
      setCountryStatusAgg([]);
      setFilteredStatusData([]);
      setFilteredCountryData([]);
      setSelectedCountryForDetails(null);
      setDetailedTableSearchText('');
      // Reset productivity pagination when date range changes
      setProductivityPage(0);
    }
  }, [filterFromDate, filterToDate]);

  // Clear handler for date range
  const handleDateRangeClear = useCallback(() => {
    setFilterFromDate('');
    setFilterToDate('');
    setDateRangeKey((k) => k + 1); // force McDateRange remount to clear UI
    setSelectedCountry(null);
    setSelectedCaseStatus(null);
    setShowStatusDetailedTable(false);
    setShowCaseStatusDistribution(false);
    setFilteredStatusData([]);
    setStatusTableSearchText('');
    setStatusDetailRows([]);
    setStatusDetailTotal(0);
    setSelectedCountryForDetails(null);
    setShowDetailedTable(false);
    setFilteredCountryData([]);
    setDetailedTableSearchText('');
    setDetailRows([]);
    setDetailTotal(0);
    setCountryStatusAgg([]);
    setProductivityPage(0);
    if (dateRange.from || dateRange.to) {
      setDateRange({ from: null, to: null });
    }
  }, [dateRange]);

  const handleClearDateRange = useCallback((e) => {
    console.log('Clear date range clicked');
    setDateRange({ from: null, to: null });
    setFilterFromDate('');
    setFilterToDate('');
    setSelectedCountry(null);
    setSelectedCaseStatus(null);
    setShowStatusDetailedTable(false);
    setShowCaseStatusDistribution(false);
    setFilteredStatusData([]);
    setStatusTableSearchText('');
    setStatusDetailRows([]);
    setStatusDetailTotal(0);
    setSelectedCountryForDetails(null);
    setShowDetailedTable(false);
    setFilteredCountryData([]);
    setDetailedTableSearchText('');
    setDetailRows([]);
    setDetailTotal(0);
    setCountryStatusAgg([]);
  }, []);

  const handleExportMetrics = useCallback(async () => {
    try {
      // Fetch all rows for export (use a large limit)
      const params = new URLSearchParams();
      if (dateRange.from) params.append('start_date', dateRange.from);
      if (dateRange.to) params.append('end_date', dateRange.to);
      params.append('limit', '50000');
      params.append('offset', '0');

      const response = await fetch(`${API_URL}/get_metrics_snapshot?${params}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const exportData = await response.json();
      const rows = Array.isArray(exportData) ? exportData : [];

      if (rows.length === 0) {
        alert("No metrics data available to export");
        return;
      }

      const headers = Object.keys(rows[0]);
      const csvRows = [headers.join(",")];

      rows.forEach((row) => {
        const values = headers.map((header) => {
          const val = row[header] ?? "";
          return `"${String(val).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(","));
      });

      const csvContent = `\uFEFF${csvRows.join('\r\n')}`;
      const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(csvData);
      const link = document.createElement("a");
      link.href = url;
      link.download = `metrics_snapshot_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting metrics:', error);
      alert('Error exporting metrics data');
    }
  }, [dateRange]);

  const handleExportExecutiveSummary = useCallback(() => {
    try {
      if (!executiveSummaryData || executiveSummaryData.length === 0) {
        alert('No Executive Summary data available to export');
        return;
      }

      const headers = ['Country', 'Date', 'Total Cases', 'SLA %', 'Compliance %', 'Non-Compliance %', 'Exception %'];
      const keys = ['country', 'date', 'totalCases', 'slaPercent', 'compliancePercent', 'nonCompliancePercent', 'exceptionPercent'];
      const csvRows = [headers.join(',')];

      executiveSummaryData.forEach((row) => {
        const values = keys.map((key) => {
          const val = row[key] ?? '';
          return `"${String(val).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });

      const csvContent = `\uFEFF${csvRows.join('\r\n')}`;
      const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(csvData);
      const link = document.createElement('a');
      link.href = url;
      link.download = `executive_summary_${selectedTrendsCountry.join('_')}_${executiveSummaryDays}days_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting executive summary:', error);
      alert('Error exporting Executive Summary data');
    }
  }, [executiveSummaryData, selectedTrendsCountry, executiveSummaryDays]);

  const handleExportDetailedCases = useCallback((rows, label, columnsToExport = null) => {
    try {
      if (!rows || rows.length === 0) {
        alert('No data available to export');
        return;
      }

      // Date fields that need formatting
      const dateFields = ['date of the request/case', 'time received', 'payment proof date', 'time closed/ responded'];

      // Determine headers: use only visible columns if columnsToExport is provided
      let headers;
      if (columnsToExport && columnsToExport.length > 0) {
        // Extract field names from column objects and filter out 'id'
        // Export all selected columns; row[header] ?? '' handles missing values
        headers = columnsToExport
          .map(col => col.field)
          .filter(k => k !== 'id')
          .sort((a, b) => {
            // Maintain column order from columnsToExport
            const aIndex = columnsToExport.findIndex(c => c.field === a);
            const bIndex = columnsToExport.findIndex(c => c.field === b);
            return aIndex - bIndex;
          });
      } else {
        // Fallback: use all keys except 'id' if no columns specified
        headers = Object.keys(rows[0]).filter(k => k !== 'id');
      }

      const csvRows = [headers.join(',')];

      rows.forEach((row) => {
        const values = headers.map((header) => {
          let val = row[header] ?? '';

          // Keep Invoice Number export stable across Excel parsers on Windows.
          if (header === 'invoicenumber' && val) {
            val = String(val)
              .split(',')
              .map(v => v.trim())
              .filter(Boolean)
              .join(', ');
          }
          
          // Apply timezone-aware date formatting for date fields
          if (dateFields.includes(header) && val) {
            // Use date-only format for payment proof date
            if (header === 'payment proof date') {
              val = formatCommaSeparatedDatesOnly(val, selectedTimezone);
            } else {
              val = formatCommaSeparatedDates(val, selectedTimezone);
            }
          }
          
          return `"${String(val).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });

      const csvContent = `\uFEFF${csvRows.join('\r\n')}`;
      const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(csvData);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${label.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting detailed cases:', error);
      alert('Error exporting data');
    }
  }, [selectedTimezone]);

  const handleExportProductivityReport = useCallback(async () => {
    try {
      if (selectedTrendsCountry.length === 0) {
        alert('Please select at least one country to export');
        return;
      }
      setExportingProductivity(true);
      console.log('[Productivity Export] Export started', {
        countries: selectedTrendsCountry,
        dateRange: { from: dateRange.from, to: dateRange.to },
        searchFilter: productivityTableSearchText.trim() || null
      });

      // Build base params matching current filters (country + date range)
      const baseParams = new URLSearchParams();
      if (dateRange.from) baseParams.append('start_date', dateRange.from);
      if (dateRange.to) {
        const [y, m, d] = dateRange.to.split('-').map(Number);
        const endDate = new Date(y, m - 1, d + 1);
        const ey = endDate.getFullYear();
        const em = String(endDate.getMonth() + 1).padStart(2, '0');
        const ed = String(endDate.getDate()).padStart(2, '0');
        baseParams.append('end_date', `${ey}-${em}-${ed}`);
      }

      // Fetch all data using parallel API calls per country (concurrency-limited)
      const countriesToExport = selectedTrendsCountry;
      const BATCH_SIZE = 10000;
      const CONCURRENCY_LIMIT = 5;
      let allRows = [];
      let apiSuccessCount = 0;
      let apiFailureCount = 0;
      let totalApiCalls = 0;

      // Helper to parse API response (handles both array and {items, total} formats)
      const parseResponse = (data) => {
        if (data.items && Array.isArray(data.items)) return data.items;
        if (Array.isArray(data)) return data;
        return [];
      };

      // Fetches all pages for a single country
      const fetchAllBatchesForCountry = async (country) => {
        const countryRows = [];
        let batchOffset = 0;
        let hasMore = true;
        while (hasMore) {
          const params = new URLSearchParams(baseParams);
          params.set('countries', country);
          params.set('limit', String(BATCH_SIZE));
          params.set('offset', String(batchOffset));
          try {
            const res = await fetch(`${API_URL}/metrics/productivity-report?${params}`);
            totalApiCalls++;
            if (!res.ok) {
              apiFailureCount++;
              console.error(`[Productivity Export] Batch API FAILED for ${country} at offset ${batchOffset} — HTTP ${res.status}`);
              break;
            }
            const data = await res.json();
            const rows = parseResponse(data);
            apiSuccessCount++;
            console.log(`[Productivity Export] Batch API SUCCESS for ${country} at offset ${batchOffset} — ${rows.length} rows`);
            countryRows.push(...rows);
            if (rows.length < BATCH_SIZE) {
              hasMore = false;
            } else {
              batchOffset += BATCH_SIZE;
            }
          } catch (err) {
            apiFailureCount++;
            totalApiCalls++;
            console.error(`[Productivity Export] Batch API ERROR for ${country} at offset ${batchOffset}`, err.message);
            break;
          }
        }
        return countryRows;
      };

      // Run country fetches in parallel with a concurrency limit
      console.log(`[Productivity Export] Fetching data for ${countriesToExport.length} countries (concurrency: ${CONCURRENCY_LIMIT})`);
      const results = [];
      for (let i = 0; i < countriesToExport.length; i += CONCURRENCY_LIMIT) {
        const batch = countriesToExport.slice(i, i + CONCURRENCY_LIMIT);
        const batchResults = await Promise.all(
          batch.map(country => fetchAllBatchesForCountry(country).catch(err => {
            apiFailureCount++;
            console.error(`[Productivity Export] API ERROR for country: ${country}`, err.message);
            return [];
          }))
        );
        results.push(...batchResults);
      }
      allRows = results.flat();

      // Log API call summary
      console.log(`[Productivity Export] API Summary: Total=${totalApiCalls}, Success=${apiSuccessCount}, Failed=${apiFailureCount}, Rows fetched=${allRows.length}`);

      if (apiFailureCount > 0) {
        console.warn(`[Productivity Export] ${apiFailureCount}/${totalApiCalls} API calls failed. Export will proceed with available data.`);
      }

      if (allRows.length === 0) {
        console.warn('[Productivity Export] No rows returned from any API call — aborting export');
        alert('No data available to export');
        setExportingProductivity(false);
        return;
      }

      // Expand rows with multiple BL numbers or multiple user IDs
      const expandedRows = [];
      allRows.forEach((row, idx) => {
        const blNumber = row['BL'] || '';
        const userId = row['USER ID'] || '';
        const hasMultipleUserIds = userId && userId.toString().includes(',');
        const hasMultipleBlNumbers = blNumber && blNumber.toString().includes(',');

        if (hasMultipleUserIds || hasMultipleBlNumbers) {
          const userIds = hasMultipleUserIds
            ? userId.toString().split(',').map(u => u.trim()).filter(u => u)
            : [userId];
          const blNumbers = hasMultipleBlNumbers
            ? blNumber.toString().split(',').map(bl => bl.trim()).filter(bl => bl)
            : [blNumber];
          userIds.forEach((u) => {
            blNumbers.forEach((bl) => {
              expandedRows.push({ ...row, 'USER ID': u, 'BL': bl });
            });
          });
        } else {
          expandedRows.push(row);
        }
      });

      // Deduplicate based on normalized user id, bl number, and case number
      const seenGroups = new Set();
      const dedupedRows = [];
      expandedRows.forEach((row, index) => {
        const userId = (row['USER ID'] || '').toLowerCase().trim();
        const blNumber = (row['BL'] || '').toLowerCase().trim();
        const caseNumber = (row['DA Case Number #'] || '').toLowerCase().trim();
        const hasCompleteGroupingFields = userId !== '' && blNumber !== '' && caseNumber !== '';
        const groupKey = hasCompleteGroupingFields
          ? `${userId}||${blNumber}||${caseNumber}`
          : `__row__||${index}`;
        if (!seenGroups.has(groupKey)) {
          seenGroups.add(groupKey);
          dedupedRows.push(row);
        }
      });

      console.log(`[Productivity Export] Row processing: Raw=${allRows.length}, Expanded=${expandedRows.length}, Deduped=${dedupedRows.length}`);

      // Apply search filter if active
      let finalRows = dedupedRows;
      if (productivityTableSearchText.trim()) {
        const s = productivityTableSearchText.trim().toLowerCase();
        finalRows = finalRows.filter(r => Object.values(r).some(v => v != null && String(v).toLowerCase().includes(s)));
        console.log(`[Productivity Export] After search filter "${productivityTableSearchText.trim()}": ${finalRows.length} rows`);
      }

      if (finalRows.length === 0) {
        console.warn('[Productivity Export] No rows remaining after filters — aborting export');
        alert('No data available to export after applying filters');
        setExportingProductivity(false);
        return;
      }

      // Generate CSV export
      const fieldsToExport = [
        'Case Creation Week', 'USER ID', 'Case Status', 'Date of the Request/Case',
        'BL', 'Email Received Time', 'Time Closed / Responded', 'Comments',
        'DA Case Number #', 'Country', 'Cluster/Area', 'SLA computation',
        'SLA compliance', 'Backlogs / Within Business Hours'
      ];
      const dateFields = ['Date of the Request/Case', 'Email Received Time', 'Time Closed / Responded'];

      const csvRows = [fieldsToExport.join(',')];
      finalRows.forEach((row) => {
        const values = fieldsToExport.map((field) => {
          let val = row[field] ?? '';
          if (dateFields.includes(field) && val) {
            val = formatCommaSeparatedDates(val, selectedTimezone);
          }
          return `"${String(val).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });

      const csvContent = `\uFEFF${csvRows.join('\r\n')}`;
      const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(csvData);
      const link = document.createElement('a');
      link.href = url;
      const label = `Productivity_Report${selectedTrendsCountry.length > 0 ? '_' + selectedTrendsCountry.join('_') : ''}`;
      link.download = `${label.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      console.log(`[Productivity Export] Export completed successfully — ${finalRows.length} rows exported to CSV`);
    } catch (error) {
      console.error('[Productivity Export] Export FAILED with error:', error.message || error);
      alert('Error exporting Productivity Report data. Please try again.');
    } finally {
      setExportingProductivity(false);
    }
  }, [dateRange, selectedTrendsCountry, selectedTimezone, productivityTableSearchText]);

  const summaryColumns = [
    { field: 'country', headerName: 'Country', width: 250 },
    { 
      field: 'totalCases', 
      headerName: 'Total Cases', 
      width: 130,
      renderCell: (params) => (
        <div 
          style={{ 
            color: '#1686BD', 
            cursor: 'pointer', 
            textDecoration: 'underline',
            fontWeight: 'bold'
          }}
          onClick={() => handleTotalCasesClick(params.row.country)}
        >
          {params.value}
        </div>
      )
    },
    { 
      field: 'year', 
      headerName: 'Year', 
      width: 120
    },
    { field: 'month', headerName: 'Month', width: 150 }    
  ];

  const executiveSummaryColumns = useMemo(() => [
    { field: 'country', headerName: 'Country', flex: 1, minWidth: 110 },
    { field: 'date', headerName: 'Date', width: 110 },
    { field: 'totalCases', headerName: 'Total Cases', width: 100, type: 'number' },
    { field: 'slaPercent', headerName: 'SLA %', width: 80, type: 'number',
      renderCell: (params) => (
        <span style={{ color: Number(params.value) >= 90 ? '#6CA926' : Number(params.value) >= 70 ? '#F3870C' : '#B80012', fontWeight: 600 }}>
          {params.value}%
        </span>
      )
    },
    { field: 'compliancePercent', headerName: 'Compliance %', width: 110, type: 'number',
      renderCell: (params) => (
        <span style={{ color: Number(params.value) >= 80 ? '#6CA926' : Number(params.value) >= 50 ? '#F3870C' : '#B80012', fontWeight: 600 }}>
          {params.value}%
        </span>
      )
    },
    { field: 'nonCompliancePercent', headerName: 'Non-Compliance %', width: 130, type: 'number',
      renderCell: (params) => (
        <span style={{ color: Number(params.value) <= 10 ? '#6CA926' : Number(params.value) <= 30 ? '#F3870C' : '#B80012', fontWeight: 600 }}>
          {params.value}%
        </span>
      )
    },
    { field: 'exceptionPercent', headerName: 'Exception %', width: 100, type: 'number',
      renderCell: (params) => (
        <span style={{ color: Number(params.value) <= 5 ? '#6CA926' : Number(params.value) <= 15 ? '#F3870C' : '#B80012', fontWeight: 600 }}>
          {params.value}%
        </span>
      )
    },
  ], []);

  // Columns sourced from afr_db.vw_afr_productivity_reporting_v2 view
  // All 14 view columns are displayed with proper formatting and timezone conversion
  const productivityReportColumns = useMemo(() => [
    { field: 'Case Creation Week', headerName: 'Case Creation Week', width: 150, type: 'number', renderCell: (params) => <div>{params.value != null ? params.value : 'N/A'}</div> },
    { field: 'USER ID', headerName: 'USER ID', width: 130, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'Case Status', headerName: 'Case Status', width: 140, renderCell: (params) => {
        const status = params.value || '';
        // Show N/A if case status is null or empty
        if (!status) {
          return (
            <div 
             style={{
               backgroundColor: '#FFF5DD',
               color: '#333',
               padding: '4px 10px',
               borderRadius: '12px',
               fontSize: '11px',
               fontWeight: '600',
               textAlign: 'center',
               display: 'inline-block'
             }}
            >
              N/A
            </div>
          );
        }
        const color = getStatusColor(status);
        return (
          <div style={{
            backgroundColor: color,
            color: ['#FFD029', '#E2E2E2', '#D4D4D4', '#F0F0F0'].includes(color) ? '#333' : '#fff',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            textAlign: 'center',
            display: 'inline-block'
          }}>
            {status}
          </div>
        );
      }},
    { field: 'Date of the Request/Case', headerName: 'Date of Request/Case', width: 200, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatDateTimeWithTimezone(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},

    { field: 'BL', headerName: 'BL', width: 150, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'Email Received Time', headerName: 'Email Received Time', width: 200, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatDateTimeWithTimezone(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},

    { field: 'Time Closed / Responded', headerName: 'Time Closed / Responded', width: 200, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatDateTimeWithTimezone(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},
    { field: 'Comments', headerName: 'Comments', width: 220, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'DA Case Number #', headerName: 'DA Case Number #', width: 160, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'Country', headerName: 'Country', width: 150, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'Cluster/Area', headerName: 'Cluster/Area', width: 150, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'SLA computation', headerName: 'SLA Computation', width: 150, renderCell: (params) => {
        if (params.value === null || params.value === undefined || params.value === '') return <div>N/A</div>;
        const val = String(params.value).split('.')[0];
        return <div>{val || 'N/A'}</div>;
      }},
    { field: 'SLA compliance', headerName: 'SLA Compliance', width: 150, renderCell: (params) => {
        return <div>{formatYesNoValue(params.value)}</div>;
      }},
    { field: 'Backlogs / Within Business Hours', headerName: 'Backlogs / Within Business Hours', width: 220, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
  ], [selectedTimezone]);

  const countryDistributionColumns = [
    { 
      field: 'country', 
      headerName: 'Country', 
      width: 300,
      renderCell: (params) => (
        <div 
          style={{ 
            color: '#1686BD', 
            cursor: 'pointer', 
            textDecoration: 'underline',
            fontWeight: 'bold'
          }}
          onClick={() => handleCountryClick({ activeLabel: params.value })}
        >
          {params.value}
        </div>
      )
    },
    { 
      field: 'totalCases', 
      headerName: 'Total Cases', 
      width: 150,
      type: 'number'
    },
    { 
      field: 'percentage', 
      headerName: 'Percentage', 
      width: 150,
      renderCell: (params) => `${params.value}%`
    }
  ];

  // Columns are memoised so they react to the selected timezone
  const columns = useMemo(() => [
    { field: 'casenumber', headerName: 'Case Number', width: 160, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'user id', headerName: 'User ID', width: 160, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'date of the request/case', headerName: 'Date of Request/Case', width: 240, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatDateTimeWithTimezone(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},

    { field: 'case creation week', headerName: 'Case Creation Week', width: 160, type: 'number', renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'bl number', headerName: 'BL Number', width: 180, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'ubli', headerName: 'UBLI', width: 130, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'invoicenumber', headerName: 'Invoice Number', width: 160, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'time received', headerName: 'Time Received', width: 240, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatDateTimeWithTimezone(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},

    { field: 'casestatus', headerName: 'Case Status', width: 145,
      renderCell: (params) => {
        const status = params.value || '';
        const color = getStatusColor(status);
        return (
          <div style={{
            backgroundColor: color,
            color: ['#FFD029', '#E2E2E2', '#D4D4D4', '#F0F0F0'].includes(color) ? '#333' : '#fff',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            textAlign: 'center',
            display: 'inline-block'
          }}>
            {status}
          </div>
        );
      }
    },
    { field: '# of times case reopened', headerName: '# Times Reopened', width: 150, type: 'number', renderCell: (params) => <div>{params.value != null ? params.value : 'N/A'}</div> },
    { field: 'comments', headerName: 'Comments', width: 220, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'payment proof reference number', headerName: 'Payment Proof Ref #', width: 200, renderCell: (params) => {
        const val = params.value;
        if (!val) return <div>N/A</div>;
        const parts = String(val).split(',').map(v => v.trim()).filter(Boolean);
        if (parts.length === 0) return <div>N/A</div>;
        const formatted = parts.map((p, i) => `POP${i + 1}-${p}`).join(', ');
        return <div title={formatted}>{formatted}</div>;
      }
    },
    { field: 'payment proof date', headerName: 'Payment Proof Date', width: 240, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatCommaSeparatedDatesOnly(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},

    { field: 'amount in payment proof', headerName: 'Amount in Payment Proof', width: 180, type: 'string',
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <div>N/A</div>;
        // Display comma-separated values as-is from database
        return <div>{String(val).trim()}</div>;
      }
    },
    { field: 'currency in payment proof', headerName: 'Currency in Payment Proof', width: 180, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'amount for invoice', headerName: 'Amount for Invoice', width: 160, type: 'string',
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return <div>N/A</div>;
        // Display comma-separated values as-is from database
        return <div>{String(val).trim()}</div>;
      }
    },
    { field: 'amount_variance', headerName: 'Amount Variance', width: 160, type: 'string', renderCell: (params) => <div>{params.value ?? 'N/A'}</div> },
    { field: 'time closed/ responded', headerName: 'Time Closed/Responded', width: 240, renderCell: (params) => {
        if (!params.value) return <div>N/A</div>;
        try {
          const formatted = formatDateTimeWithTimezone(params.value, selectedTimezone);
          return <div title={formatted}>{formatted}</div>;
        } catch { return <div>{params.value}</div>; }
      }},
    { field: 'currency for invoice', headerName: 'Currency for Invoice', width: 160, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'beneficiary name', headerName: 'Beneficiary Name', width: 200, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'bank account number', headerName: 'Bank Account Number', width: 180, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'customercmdcode', headerName: 'Customer CMD Code', width: 170, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'bpnumber', headerName: 'BP Number', width: 140, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'contract account', headerName: 'Contract Account', width: 170, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'payer name', headerName: 'Payer Name', width: 200, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'country name', headerName: 'Country', width: 140, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'cluster name', headerName: 'Cluster', width: 130, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'sla computation', headerName: 'SLA Computation', width: 180, renderCell: (params) => {
        if (params.value === null || params.value === undefined || params.value === '') return <div>N/A</div>;
        const val = String(params.value).split('.')[0];
        return <div>{val || 'N/A'}</div>;
      }},
    { field: 'sla_compliance', headerName: 'SLA Compliance', width: 180, renderCell: (params) => {
        return <div>{formatYesNoValue(params.value)}</div>;
      }},
    { field: 'Backlogs / Within Business Hours', headerName: 'Backlogs / Within Business Hours', width: 220, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'invoicetype', headerName: 'Invoice Type', width: 130, renderCell: (params) => <div>{params.value || 'N/A'}</div> },
    { field: 'AFR Closed', headerName: 'AFR Closed', width: 130,
      renderCell: (params) => {
        const value = params.value || 'No';
        const bgColor = value === 'Yes' ? '#42B0D5' : '#E2E2E2';
        const textColor = value === 'Yes' ? '#fff' : '#333';
        return (
          <div style={{
            backgroundColor: bgColor,
            color: textColor,
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            textAlign: 'center',
            display: 'inline-block'
          }}>
            {value}
          </div>
        );
      }
    },
    { field: 'nit number', headerName: 'Tax ID Number / NIT', width: 160, renderCell: (params) => <div>{params.value || 'N/A'}</div> }
  ], [selectedTimezone]);

  // Column field sets per Purpose
  const PURPOSE_FIELD_MAP = useMemo(() => ({
    SLA: [
      'case creation week', 'user id', 'date of the request/case', 'bl number',
      'time received', 'casestatus', '# of times case reopened', 'sla computation', 'sla_compliance', 'Backlogs / Within Business Hours', 'AFR Closed', 'nit number'
    ],
    Cashapp: [
      'ubli', 'invoicenumber', 'comments', 'payment proof reference number',
      'payment proof date', 'amount in payment proof', 'currency in payment proof',
      'amount for invoice', 'amount_variance', 'time closed/ responded', 'currency for invoice', 'bank account number',
      'bpnumber', 'contract account', 'payer name', 'invoicetype', 'AFR Closed', 'nit number'
    ],
    'Cashapp/SLA': [
      'customercmdcode', 'casenumber', 'country name', 'cluster name', 'AFR Closed', 'nit number'
    ]
  }), []);

  // Filtered columns based on selected Purpose
  const filteredColumns = useMemo(() => {
    if (selectedPurpose === 'All') return columns;
    const allowedFields = PURPOSE_FIELD_MAP[selectedPurpose];
    if (!allowedFields) return columns;
    return columns.filter(col => allowedFields.includes(col.field));
  }, [selectedPurpose, columns, PURPOSE_FIELD_MAP]);

  // Drilldown handler: when a trend data-point is clicked, fetch per-day data for the full selected date range and open modal
  const handleTrendsDrilldown = useCallback(async (status) => {
    if (!status) return;

    // Helper: format a local Date as YYYY-MM-DD without UTC shift
    const formatLocalDate = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Priority: use custom date range if set, else fallback to Date Range Filter
    let start, end, startStr, endStr, weekLabel;
    if (dateRange.from && dateRange.to) {
      // Use custom date range
      start = new Date(dateRange.from);
      end = new Date(dateRange.to);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      startStr = dateRange.from;
      endStr = dateRange.to;
      weekLabel = `${dateRange.from} to ${dateRange.to}`;
    } else {
      // Fallback to Date Range Filter logic
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      end = new Date(today);
      end.setDate(end.getDate() - 1); // today-1
      start = new Date(end);
      start.setDate(start.getDate() - (executiveSummaryDays - 1));
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      startStr = formatLocalDate(start);
      endStr = formatLocalDate(end);
      weekLabel = `${startStr} to ${endStr}`;
    }

   try {
      // Use lightweight aggregation endpoint instead of fetching raw rows
      const params = new URLSearchParams();
      params.append('countries', selectedTrendsCountry.join(','));
      params.append('casestatus', status);
      params.append('start_date', startStr);
      params.append('end_date', endStr);


      const res = await fetch(`${API_URL}/metrics/daily-breakdown?${params}`);
      const data = res.ok ? await res.json() : [];
      const rows = Array.isArray(data) ? data : [];


      // Build countryDayMaps from aggregated response: [{country, date, count}]
      const countryDayMaps = {};
      rows.forEach(item => {
        const country = item.country;
        const dayKey = item.date;
        if (!countryDayMaps[country]) countryDayMaps[country] = {};
        countryDayMaps[country][dayKey] = (countryDayMaps[country][dayKey] || 0) + item.count;
      });

      // Build per-country dailyData arrays with zero-filled for each day in the selected range
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const perCountryData = {};

      selectedTrendsCountry.forEach(country => {
        const dayMap = countryDayMaps[country] || {};
        const dailyData = [];
        for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
          const dayKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
          const dayName = dayNames[dt.getDay()];
          dailyData.push({
            date: dayKey,
            dayLabel: `${dayName} ${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`,
            cases: dayMap[dayKey] || 0
          });
        }
        perCountryData[country] = dailyData;
      });

      setTrendsDrilldownStatus(status);
      setTrendsDrilldownWeekLabel(weekLabel);
      setTrendsDrilldownDailyData(perCountryData);
      setTrendsDrilldownOpen(true);
    } catch (err) {
      console.error('Failed to load drilldown data:', err);
    }
  }, [selectedTrendsCountry, executiveSummaryDays, dateRange]);

  // Dashboard Card Component
  const DashboardCard = ({ title, value, unit, trend, icon, color, onClick, onDataPointClick }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e0e0e0',
      padding: '15px',
      width: '230px',
      height: '220px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '5px'
      }}>
        <span style={{
          fontSize: '14px',
          color: '#666',
          fontWeight: '500',
          fontFamily: 'Maersk Text'
        }}>
          {title}
        </span>
        <div style={{
          backgroundColor: color,
          borderRadius: '8px',
          padding: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span style={{ fontSize: '20px', filter: 'grayscale(0)' }}>{icon}</span>
        </div>
      </div>

      {/* Value */}
      <div style={{
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '16px',
        fontFamily: 'Maersk Text'
      }}>
        {value}
        {unit && <span style={{ fontSize: '12px', color: '#666', marginLeft: '4px' }}>({unit})</span>}
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: '120px', marginBottom: '16px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={Array.isArray(trend) && trend.length > 0 && typeof trend[0] === 'object'
              ? trend
              : trend.map((val, index) => ({ week: `W${index + 1}`, value: Math.max(val, 0) }))
            }
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
          >
            <defs>
              <linearGradient id={`gradient-${title.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.6}/>
                <stop offset="100%" stopColor={color} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="week"
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#999' }}
              angle={0}
              textAnchor="end"
            />
            <YAxis
              domain={[0, 'dataMax + 1']}
              tick={{ fontSize: 12 }}
            />
            <RechartsTooltip
              contentStyle={{ fontSize: '12px', fontFamily: 'Maersk Text' }}
              formatter={(val) => [`${val} Cases`, '']}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${title.replace(/\s+/g, '')})`}
              activeDot={{
                r: 7,
                fill: color,
                stroke: '#fff',
                strokeWidth: 2,
                cursor: 'pointer',
                onClick: (e, payload) => {
                  if (onDataPointClick && payload && payload.payload) {
                    onDataPointClick(payload.payload.week);
                  }
                }
              }}
              dot={{ r: 3, fill: color, cursor: 'pointer' }}
              connectNulls={true}
              isAnimationActive={true}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // Display helpers
  const selectedCountryLabel = selectedTrendsCountry.length > 0 ? selectedTrendsCountry.join(', ') : '';
  const selectedCountryFlag = selectedTrendsCountry.length === 1 ? (COUNTRY_FLAG_MAP[selectedTrendsCountry[0]] || '🌐') : '🌐';

  return (
    <div style={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Header Bar */}
      {/* <div style={{
        background: 'white', display: 'flex', justifyContent: 'space-between',
        padding: '16px 24px', alignItems: 'center', position: 'sticky', top: 0,
        zIndex: 1000, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        borderRadius: '12px', margin: '12px 16px 0 16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '22px', color: '#42B0D5' }}>✦</span>
          <span style={{ fontFamily: 'Maersk Text', fontSize: '18px', fontWeight: '700', color: '#141414' }}>
            Metrics Snapshot
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={() => fetchInitialData()} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 20px', borderRadius: '8px',
              border: '1px solid #d1d5db', background: 'white', cursor: 'pointer',
              fontFamily: 'Maersk Text', fontSize: '14px', fontWeight: '500', color: '#333' }}>
            Refresh
          </button>
          <button onClick={handleExportMetrics} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 20px', borderRadius: '8px',
              border: '1px solid #d1d5db', background: loading ? '#e5e7eb' : 'white', cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'Maersk Text', fontSize: '14px', fontWeight: '500', color: loading ? '#9ca3af' : '#333',
              opacity: loading ? 0.7 : 1 }}>
            Export
          </button>
        </div>
      </div> */}

      <div style={{ padding: '10px' }}>

        {/* Filters — always visible, never unmounted */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'stretch', position: 'sticky', top: 0, zIndex: 10, background: '#f4f6f8' }}>
          {/* Filters — left side */}
          <div style={{
            flex: 1, background: 'white', borderRadius: '12px', padding: '5px 10px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center'
          }}>
            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <SearchIcon style={{ color: '#666', fontSize: '18px' }} />
              <span style={{ fontFamily: 'Maersk Text', fontSize: '14px', fontWeight: '700', color: '#333' }}>Filters</span>
            </div> */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
              {/* Country Selector - hidden on Detailed Summary tab */}
              {activeTab !== 1 && (
              <div style={{ minWidth: 220 }}>
                <McMultiSelect
                  ref={countryMultiSelectRef}
                  label="Select Country"
                  placeholder="No countries selected"
                  name="countryFilter"
                  value={selectedTrendsCountry.join(',')}
                >
                  {availableCountries.map((country) => (
                    <McOption key={country} value={country} selected={selectedTrendsCountry.includes(country)}>
                      {country}
                    </McOption>
                  ))}
                </McMultiSelect>
              </div>
              )}
              {/* Date Range Filter */}
              <div style={{ minWidth: 280 }}>
                <McDateRange
                  key={dateRangeKey}
                  label="Date Range"
                  placeholder="YYYY-MM-DD"
                  fromlabel="From"
                  tolabel="To"
                  name="metricsDateRange"
                  value={filterFromDate && filterToDate ? { from: filterFromDate, to: filterToDate } : undefined}
                  {...(activeTab === 2 ? { min: oneMonthAgo } : activeTab === 1 ? { min: tenDaysAgo } : {})}
                  click={handleDateRangeChange}
                  clearbuttonclick={handleDateRangeClear}
                />
              </div>
              {/* Apply Button */}
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                style={{
                  padding: '7px 24px', borderRadius: '8px', border: 'none',
                  background: loading ? '#93d1e8' : '#42B0D5', color: 'white', fontFamily: 'Maersk Text',
                  fontSize: '13px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', height: '38px'
                }}
              >
                Apply
              </button>
              {/* Clear Button */}
              {(filterFromDate || filterToDate) && (
                <button
                  onClick={handleDateRangeClear}
                  disabled={loading}
                  style={{
                    padding: '7px 24px', borderRadius: '8px', border: '1px solid #d1d5db',
                    background: 'white', color: '#333', fontFamily: 'Maersk Text',
                    fontSize: '13px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', height: '38px'
                  }}
                >
                  Clear
                </button>
              )}

              {/* Total Cases: Only show if not on Detailed Summary tab */}
              {activeTab !== 1 && (
                <div style={{ minWidth: '200px', marginLeft: '20px'}}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: '600', color: '#333' }}>Total Cases</span>
                  </div>
                  <div style={{ fontFamily: 'Maersk Text', fontSize: '26px', fontWeight: '700', color: '#141414', minHeight: '32px' }}>
                    {(loading || (activeTab === 0 && summaryMetrics.totalCases === 0 && !isInitialLoad) || (activeTab === 2 && loadingProductivityData)) ? (
                      <span style={{ color: '#aaa' }}>...</span>
                    ) : (
                      activeTab === 2 ? (
                        productivityTotalCases.toLocaleString()
                      ) : (
                        summaryMetrics.totalCases ? summaryMetrics.totalCases.toLocaleString() : <span style={{ color: '#aaa' }}>...</span>
                      )
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
        
        {/* Loading State - loader for content below filters */}
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '600px',
            flexDirection: 'column' 
          }}>
            <McLoadingIndicator
              appearance="primary"
              style={{ fontSize: "36px", transform: "scale(2)", marginBottom: "16px" }}
            />
            <span style={{ color: "#6A6A6A", fontSize: "16px" }}>
              {isInitialLoad ? "Loading metrics data..." : "Processing data..."}
            </span>
            <span style={{ color: "#999", fontSize: "12px", marginTop: "8px" }}>
              {isInitialLoad ? "Fetching data from server" : "Preparing charts and tables"}
            </span>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            {/* <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'stretch' }}> */}
              {/* Total Cases: Only show if not on Detailed Summary tab */}
              {/* {activeTab !== 1 && (
                <div style={{ minWidth: '200px', background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📋</span>
                    <span style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: '600', color: '#333' }}>Total Cases</span>
                  </div>
                  <div style={{ fontFamily: 'Maersk Text', fontSize: '26px', fontWeight: '700', color: '#141414', minHeight: '32px' }}>
                    {(loading || summaryMetrics.totalCases === 0 && !isInitialLoad) ? (
                      <span style={{ color: '#aaa' }}>...</span>
                    ) : (
                      summaryMetrics.totalCases ? summaryMetrics.totalCases.toLocaleString() : <span style={{ color: '#aaa' }}>...</span>
                    )}
                  </div>
                </div>
              )} */}
              {/* Countries */}
              {/* <div style={{ minWidth: '160px', background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>{selectedCountryFlag}</span>
                  <span style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: '600', color: '#333' }}>Countries</span>
                </div>
                <div style={{ fontFamily: 'Maersk Text', fontSize: '26px', fontWeight: '700', color: '#141414' }}>
                  {summaryMetrics.totalCountries}
                </div>
              </div> */}
              {/* Clusters */}
              {/* <div style={{ minWidth: '110px', background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🔲</span>
                  <span style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: '600', color: '#333' }}>Clusters</span>
                </div>
                <div style={{ fontFamily: 'Maersk Text', fontSize: '26px', fontWeight: '700', color: '#141414' }}>
                  {summaryMetrics.totalClusters}
                </div>
              </div> */}
              {/* Case Statuses */}
              {/* <div style={{ minWidth: '140px', background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>☰</span>
                  <span style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: '600', color: '#333' }}>Case Statuses</span>
                </div>
                <div style={{ fontFamily: 'Maersk Text', fontSize: '26px', fontWeight: '700', color: '#141414' }}>
                  {summaryMetrics.totalStatuses}
                </div>
              </div> */}
            {/* </div> */}

            {/* Tab Interface */}
            <Box sx={{ width: '100%', marginBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="metrics tabs"
                sx={{
                  '& .MuiTab-root': {
                    fontFamily: 'Maersk Text',
                    fontSize: '16px',
                    fontWeight: '600',
                    textTransform: 'none',
                    minWidth: '140px',
                    color: '#999',
                    padding: '12px 24px',
                  },
                  '& .Mui-selected': {
                    color: '#141414 !important',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#42B0D5',
                    height: '3px',
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab label="Trends" />
                <Tab label="Detailed Summary" />
                <Tab label="Productivity Report" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
              // Trends Tab Content — 50 / 50 layout
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

                {/* ═══ LEFT 50 % — Case Status Trends ═══ */}
                <div style={{ flex: '1 1 60%', minWidth: 0, height: '600px', display: 'flex', flexDirection: 'column' }}>
                  {/* Section Header + Date Filter */}
                  <div style={{ 
                    display: "flex", 
                    // justifyContent: "space-between", 
                    alignItems: "center",
                    marginBottom: "5px",
                    flexWrap: "wrap",
                    gap: "10px",
                    flexShrink: 0,
                  }}>
                    <h3 style={{ 
                      margin: "0", 
                      fontSize: "15px", 
                      fontWeight: "600",
                      color: "#003E62",
                      fontFamily: "Maersk Text",
                      width: '80%'
                    }}>
                      Case Status Trends{selectedCountryLabel ? ` in ${selectedCountryLabel}` : ''}
                    </h3>
                    <FormControl size="small" style={{ minWidth: 140, position: 'relative' }}>
                      <InputLabel
                        id="exec-days-label"
                        style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                      >
                        Date Filter
                      </InputLabel>
                      <Select
                        labelId="exec-days-label"
                        value={executiveSummaryDays}
                        label="Date Filter"
                        onChange={(e) => setExecutiveSummaryDays(Number(e.target.value))}
                        style={{ fontFamily: 'Maersk Text', fontSize: '13px', borderRadius: '8px' }}
                        disabled={!!(dateRange.from && dateRange.to)}
                        MenuProps={{
                          PaperProps: {
                            style: { backgroundColor: (dateRange.from && dateRange.to) ? '#f5f5f5' : 'white' }
                          }
                        }}
                      >
                        <MenuItem value={7} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 7 days</MenuItem>
                        <MenuItem value={14} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 14 days</MenuItem>
                        <MenuItem value={30} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 30 days</MenuItem>
                        <MenuItem value={60} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 60 days</MenuItem>
                        <MenuItem value={90} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 90 days</MenuItem>
                        <MenuItem value={180} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 180 days</MenuItem>
                        <MenuItem value={365} style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Last 365 days</MenuItem>
                      </Select>
                      {dateRange.from && dateRange.to && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: 2 }}>
                          Custom date range applied
                        </div>
                      )}
                    </FormControl>
                  </div>

                  {/* Scrollable cards area */}
                  <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

                  {/* Dashboard Cards Section - Case Status Trends */}
                  {selectedTrendsCountry.length > 0 && Object.keys(dashboardMetrics).length > 0 && (
                    <div style={{ 
                      display: "flex", 
                      marginBottom: "30px",
                      gap: "20px",
                      flexWrap: "wrap"
                    }}>
                      {Object.entries(dashboardMetrics).length > 0 ? (
                        Object.entries(dashboardMetrics).map(([key, metric]) => (
                          <DashboardCard
                            key={key}
                            title={metric.title}
                            value={metric.value}
                            unit={metric.unit}
                            trend={metric.trend}
                            icon={metric.icon}
                            color={metric.color}
                            onClick={() => console.log(`${metric.title} details clicked`)}
                            onDataPointClick={() => handleTrendsDrilldown(metric.originalStatus)}
                          />
                        ))
                      ) : (
                        <div style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                          width: '100%',
                          color: '#6c757d'
                        }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
                          <p style={{ margin: '0', fontSize: '14px' }}>
                            No case status data available for {selectedTrendsCountry.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No data message when country is selected but no trends data */}
                  {selectedTrendsCountry.length > 0 && Object.keys(dashboardMetrics).length === 0 && (
                    trendsData === null ? (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        minHeight: '300px',
                        color: '#6A6A6A',
                        fontSize: '14px',
                      }}>
                        <McLoadingIndicator
                          appearance="primary"
                          style={{ fontSize: "36px", transform: "scale(2)", marginBottom: "16px" }}
                        />
                        <span style={{ color: "#6A6A6A", fontSize: "16px" }}>
                          {isInitialLoad ? "Loading metrics data..." : "Processing data..."}
                        </span>
                        <span style={{ color: "#999", fontSize: "12px", marginTop: "8px" }}>
                          {isInitialLoad ? "Fetching data from server" : "Preparing charts and tables"}
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#6c757d',
                        fontSize: '14px',
                        flexDirection: 'column',
                        minHeight: '300px',
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                        <p style={{ margin: 0 }}>No data in the last {executiveSummaryDays} days for {selectedTrendsCountry.join(', ')}.</p>
                      </div>
                    )
                  )}

                  {/* Placeholder when no country is selected */}
                  {selectedTrendsCountry.length === 0 && (
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      border: '2px dashed #dee2e6',
                      borderRadius: '8px',
                      padding: '40px',
                      textAlign: 'center',
                      marginBottom: '30px',
                      color: '#6c757d'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                      <h4 style={{ margin: '0 0 8px 0', color: '#495057' }}>Select a Country to View Case Status Trends</h4>
                      <p style={{ margin: '0', fontSize: '14px' }}>
                        Choose a country from the dropdown above to see detailed case status trends with weekly data visualization.
                      </p>
                    </div>
                  )}

                {/* Line Chart for Case Status Trends */}
                {/* {selectedTrendsCountry && trendsLineChartData.length > 0 ? (
                  <div style={{ 
                    width: "100%",
                    marginBottom: "20px"
                  }}>
                    <ResponsiveContainer width="100%" height={500}>
                      <LineChart
                        data={trendsLineChartData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 120,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="timeFormatted" 
                          fontSize={12}
                          stroke="#363636"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          fontSize={12}
                          stroke="#363636"
                          label={{ value: 'Number of Cases', angle: -90, position: 'insideLeft' }}
                        />
                        <RechartsTooltip 
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                          formatter={(value, name) => [value, `${name} Cases`]}
                          labelFormatter={(label) => `${trendsTimePeriod === 'weekly' ? 'Week' : 'Month'}: ${label}`}
                        />
                        <Legend 
                          layout="horizontal"
                          align="center"
                          verticalAlign="bottom"
                          iconType="line"
                          content={(props) => {
                            const { payload } = props;
                            return (
                              <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: '20px',
                                paddingTop: '20px',
                                fontSize: '12px'
                              }}>
                                {payload.map((entry, index) => (
                                  <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}>
                                    <div style={{
                                      width: '12px',
                                      height: '2px',
                                      backgroundColor: entry.color,
                                      borderRadius: '1px'
                                    }}></div>
                                    <span>{entry.value}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />                        
                        
                        {trendsCountryStatuses.map((status, index) => (
                          <Line
                            key={status}
                            type="monotone"
                            dataKey={status}
                            stroke={STATUS_COLORS[status] || `hsl(${index * 360 / trendsCountryStatuses.length}, 70%, 50%)`}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            name={status}
                            connectNulls={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : selectedTrendsCountry ? (
                  <div style={{
                    height: "400px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#6A6A6A",
                    fontSize: "14px",
                    flexDirection: "column"
                  }}>
                    <p>No {trendsTimePeriod} trend data available for {selectedTrendsCountry}</p>
                    <p style={{ fontSize: "12px", marginTop: "10px" }}>
                      This might be due to insufficient date information in the case data.
                    </p>
                  </div>
                ) : (
                  <div style={{
                    height: "400px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#6A6A6A",
                    fontSize: "14px",
                    flexDirection: "column"
                  }}>
                    Loading monthly trends data...
                  </div>
                )} */}
                </div>{/* end scrollable cards area */}
                </div>{/* end LEFT 50% */}

                {/* ═══ RIGHT 50 % — Executive Summary ═══ */}
                <div style={{
                  flex: '1 1 50%',
                  minWidth: 0,
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {/* Header row: title + Export */}
                  <div style={{
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#333',
                      fontFamily: 'Maersk Text'
                    }}>
                      Executive Summary
                    </h3>
                    {executiveSummaryData.length > 0 && (
                      <McButton
                        label="Export"
                        appearance="secondary"
                        click={handleExportExecutiveSummary}
                      />
                    )}
                  </div>

                  {/* Executive Summary DataGrid */}
                  {selectedTrendsCountry.length > 0 ? (
                    loadingExecutiveSummary ? (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#6A6A6A',
                        fontSize: '14px',
                        flexDirection: 'column',
                        minHeight: '300px',
                      }}>
                        <McLoadingIndicator
                          appearance="primary"
                          style={{ fontSize: '36px', transform: 'scale(2)', marginBottom: '16px' }}
                        />
                        <span style={{ color: '#6A6A6A', fontSize: '16px' }}>
                          {isInitialLoad ? 'Loading executive summary...' : 'Refreshing executive summary...'}
                        </span>
                        <span style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                          Fetching summary data for the selected country and date filters
                        </span>
                      </div>
                    ) : executiveSummaryData.length > 0 ? (
                      <div style={{ height: '500px', width: '100%', overflowY: 'scroll' }}>
                        <DataGrid
                          key={`exec-summary-${executiveSummaryDays}`}
                          rows={executiveSummaryData}
                          columns={executiveSummaryColumns}
                          pageSize={15}
                          rowsPerPageOptions={[10, 15, 25, 50]}
                          disableSelectionOnClick
                          disableRowSelectionOnClick
                          density="compact"
                          initialState={{
                            sorting: {
                              sortModel: [{ field: 'date', sort: 'desc' }],
                            },
                          }}
                          sx={{
                            fontFamily: 'Maersk Text',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            '& .MuiDataGrid-columnHeaders': {
                              backgroundColor: '#f0f4f8',
                              fontWeight: 700,
                              fontSize: '12px',
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                            },
                            '& .MuiDataGrid-virtualScrollerContent': {
                              backgroundColor: 'white',
                            },
                            '& .MuiDataGrid-cell': {
                              fontSize: '12px',
                            },
                            '& .MuiDataGrid-columnHeader': {
                              fontSize: '12px',
                            },
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#6c757d',
                        fontSize: '14px',
                        flexDirection: 'column',
                        minHeight: '300px',
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                        <p style={{ margin: 0 }}>No data in the last {executiveSummaryDays} days for {selectedTrendsCountry.join(', ')}.</p>
                      </div>
                    )
                  ) : (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#6c757d',
                      fontSize: '14px',
                      flexDirection: 'column',
                      minHeight: '300px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px dashed #dee2e6',
                    }}>
                      <div style={{ fontSize: '36px', marginBottom: '12px' }}>📋</div>
                      <h4 style={{ margin: '0 0 6px 0', color: '#495057' }}>Select a Country</h4>
                      <p style={{ margin: 0, fontSize: '13px' }}>Choose a country from the dropdown above to view the Executive Summary.</p>
                    </div>
                  )}
                </div>{/* end RIGHT 50% */}

              </div>
            )}

            {activeTab === 1 && (
              // Detailed Summary Tab Content
              <div>
                {/* Country-wise + Case Status — side by side row */}
                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

                {/* Country-wise Total Cases Chart */}
                <div style={{ 
                  flex: selectedCountry && yearWiseChartData.length > 0 && showCaseStatusDistribution ? '1 1 50%' : '1 1 100%',
                  minWidth: 0,
                  transition: 'flex 0.3s ease'
                }}>
                  <div style={{ 
                    //padding: "20px", 
                    width: "100%",
                  }}>
                      {/* Header with Title and View Toggle Icons */}
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "5px"
                      }}>
                        <h3 style={{ 
                          margin: "0", 
                          fontSize: "15px", 
                          fontWeight: "600",
                          color: "#333"
                        }}>
                          Country-wise Total Cases Distribution
                        </h3>
                        
                        {/* View Toggle Icons */}
                        <div style={{ display: "flex", gap: "5px" }}>
                          <Tooltip title="Chart View">
                            <IconButton
                              onClick={() => setCountryDistributionView('chart')}
                              style={{
                                backgroundColor: countryDistributionView === 'chart' ? '#1686BD' : 'transparent',
                                color: countryDistributionView === 'chart' ? 'white' : '#1686BD',
                                border: `1px solid #1686BD`,
                                borderRadius: '4px',
                                padding: '8px'
                              }}
                            >
                              <BarChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Table View">
                            <IconButton
                              onClick={() => setCountryDistributionView('table')}
                              style={{
                                backgroundColor: countryDistributionView === 'table' ? '#1686BD' : 'transparent',
                                color: countryDistributionView === 'table' ? 'white' : '#1686BD',
                                border: `1px solid #1686BD`,
                                borderRadius: '4px',
                                padding: '8px'
                              }}
                            >
                              <TableChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                      
                      {/* Chart View - Defer rendering until after initial load */}
                      {countryDistributionView === 'chart' && chartData.length > 0 && !isInitialLoad ? (
                        <ResponsiveContainer width="100%" height={500}>
                          <BarChart
                            data={chartData}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 150,
                            }}
                            barCategoryGap="20%"
                            onClick={handleCountryClick}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis 
                              dataKey="country" 
                              angle={-45}
                              textAnchor="end"
                              height={100}
                              fontSize={12}
                              stroke="#363636"
                            />
                            <YAxis 
                              fontSize={12}
                              stroke="#363636"
                            />
                            <RechartsTooltip 
                              cursor={false}
                              contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}
                              formatter={(value, name) => [value, `Total Cases`]}
                              labelFormatter={(label) => `Country: ${label}`}
                            />
                            <Legend 
                              layout="horizontal"
                              align="center"
                              verticalAlign="bottom"
                              iconType="rect"
                              content={(props) => {
                                return (
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                    gap: '15px',
                                    paddingTop: '20px',
                                    fontSize: '12px'
                                  }}>
                                    {chartData.map((entry, index) => (
                                      <div key={index} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => handleCountryClick({ activeLabel: entry.country })}
                                      >
                                        <div style={{
                                          width: '12px',
                                          height: '12px',
                                          backgroundColor: entry.fill,
                                          borderRadius: '2px'
                                        }}></div>
                                        <span>{entry.country} ({entry.totalCases})</span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }}
                            />
                            <Bar
                              dataKey="totalCases"
                              name="Total Cases"
                              radius={[4, 4, 0, 0]}
                              cursor="pointer"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : countryDistributionView === 'chart' && isInitialLoad ? (
                        <div style={{
                          height: "500px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "#6A6A6A",
                          fontSize: "14px",
                          flexDirection: "column"
                        }}>
                          <McLoadingIndicator
                            appearance="primary"
                            style={{ fontSize: "24px", transform: "scale(1.5)", marginBottom: "16px" }}
                          />
                          <span>Preparing chart...</span>
                        </div>
                      ) : countryDistributionView === 'table' && countryDistributionTableData.length > 0 ? (
                        /* Table View */
                        <div style={{ height: '300px', width: '100%', overflowY: 'scroll' }}>
                          <DataGrid
                            rows={countryDistributionTableData}
                            columns={countryDistributionColumns}
                            pageSize={25}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            disableSelectionOnClick
                            disableRowSelectionOnClick
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'totalCases', sort: 'desc' }],
                              },
                            }}
                            sx={{
                              fontFamily: "Maersk Text",
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#ededed",
                              },
                              "& .MuiDataGrid-virtualScrollerContent": {
                                backgroundColor: "white",
                              },
                              "& .MuiDataGrid-cell": {
                                fontSize: "12px",
                              },
                              "& .MuiDataGrid-columnHeader": {
                                fontSize: "12px",
                              },
                            }}
                            loading={loading}
                            density="compact"
                          />
                        </div>
                      ) : (
                        <div style={{
                          height: "400px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "#6A6A6A",
                          fontSize: "14px",
                          flexDirection: "column"
                        }}>
                          No data available for visualization
                        </div>
                      )}
                    </div>
                </div>

                {/* Case Status Distribution Chart — beside country chart */}
                {selectedCountry && yearWiseChartData.length > 0 && showCaseStatusDistribution && (
                  <div style={{ 
                    flex: '1 1 50%',
                    minWidth: 0
                  }}>
                    {/* Header with Title and Clear Button */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "5px"
                    }}>
                      <h3 style={{ 
                        margin: "0", 
                        fontSize: "15px", 
                        fontWeight: "600",
                        color: "#333"
                      }}>
                        Case Status Distribution for {selectedCountry} ({selectedDetailedSummaryYear})
                      </h3>
                      
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Reset Status Filter — only visible when a status is selected */}
                        {selectedCaseStatus && (
                          <McButton
                            label="Reset Status"
                            appearance="secondary"
                            click={() => {
                              setSelectedCaseStatus(null);
                              setShowStatusDetailedTable(false);
                              setFilteredStatusData([]);
                              setStatusTableSearchText('');
                              // Re-show country-filtered table
                              if (selectedCountry) {
                                setSelectedCountryForDetails(selectedCountry);
                                setShowDetailedTable(true);
                                fetchDetailRows({ country: selectedCountry });
                              }
                            }}
                          />
                        )}
                        <McButton
                          label="Clear Selection"
                          appearance="secondary"
                          click={() => {
                            setSelectedCountry(null);
                            setSelectedCaseStatus(null);
                            setShowStatusDetailedTable(false);
                            setShowCaseStatusDistribution(false);
                            setFilteredStatusData([]);
                            setShowDetailedTable(false);
                            setSelectedCountryForDetails(null);
                            setFilteredCountryData([]);
                            setDetailedTableSearchText('');
                            setStatusTableSearchText('');
                          }}
                        />
                      </div>
                    </div>
                    
                    {loadingCountryStatusAgg ? (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '500px',
                        flexDirection: 'column' 
                      }}>
                        <McLoadingIndicator
                          appearance="primary"
                          style={{ fontSize: "24px", transform: "scale(1.5)", marginBottom: "16px" }}
                        />
                        <span style={{ color: "#6A6A6A", fontSize: "14px" }}>
                          Loading case status distribution for {selectedCountry}...
                        </span>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={500}>
                      <BarChart
                        data={yearWiseChartData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 120,
                        }}
                        barCategoryGap="20%"
                        onClick={handleCaseStatusClick}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="status" 
                          fontSize={12}
                          stroke="#363636"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                        />
                        <YAxis 
                          fontSize={12}
                          stroke="#363636"
                          label={{ value: 'Number of Cases', angle: -90, position: 'insideLeft' }}
                        />
                        <RechartsTooltip 
                          cursor={false}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                          formatter={(value, name) => [value, `Cases`]}
                          labelFormatter={(label) => `Status: ${label}`}
                        />
                        <Legend 
                          layout="horizontal"
                          align="center"
                          verticalAlign="bottom"
                          iconType="rect"
                          content={(props) => {
                            return (
                              <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: '20px',
                                paddingTop: '20px',
                                fontSize: '12px'
                              }}>
                                {yearWiseChartData.map((entry, index) => (
                                  <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}>
                                    <div style={{
                                      width: '12px',
                                      height: '12px',
                                      backgroundColor: getStatusColor(entry.status),
                                      borderRadius: '2px'
                                    }}></div>
                                    <span>{entry.status} ({entry.count})</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        
                        <Bar
                          dataKey="count"
                          name="Cases"
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                        >
                          {yearWiseChartData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={getStatusColor(entry.status)} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    )}
                  </div>
                )}

                </div>{/* end side-by-side row */}

                {/* Status-Specific Detailed Table - Shows when drilling down into a specific case status */}
                {showStatusDetailedTable && selectedCaseStatus && selectedCountry && (
                  <div style={{ marginTop: "20px" }}>
                    {/* Table Header with Navigation */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                          <h3 style={{ 
                            margin: '0', 
                            fontSize: '18px', 
                            fontWeight: '600',
                            color: '#333'
                          }}>
                            {selectedCaseStatus} Cases for {selectedCountry} ({selectedDetailedSummaryYear})
                          </h3>

                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            {/* Purpose Dropdown */}
                            <FormControl variant="standard" style={{ minWidth: 180 }}>
                              <InputLabel
                                id="purpose-select-label-status"
                                style={{ fontFamily: 'Maersk Text', fontSize: '14px' }}
                              >
                                Purpose
                              </InputLabel>
                              <Select
                                labelId="purpose-select-label-status"
                                id="purpose-select-status"
                                value={selectedPurpose}
                                onChange={(e) => setSelectedPurpose(e.target.value)}
                                label="Purpose"
                                style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                              >
                                <MenuItem value="All" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>All</MenuItem>
                                <MenuItem value="SLA" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>SLA</MenuItem>
                                <MenuItem value="Cashapp" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Cashapp</MenuItem>
                                <MenuItem value="Cashapp/SLA" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Cashapp/SLA</MenuItem>
                              </Select>
                            </FormControl>

                            {/* Timezone Dropdown */}
                            <FormControl variant="standard" style={{ minWidth: 260 }}>
                              <InputLabel
                                id="tz-select-label"
                                style={{ fontFamily: 'Maersk Text', fontSize: '14px' }}
                              >
                                Display Timezone
                              </InputLabel>
                              <Select
                                labelId="tz-select-label"
                                id="tz-select"
                                value={selectedTimezone}
                                onChange={(e) => setSelectedTimezone(e.target.value)}
                                label="Display Timezone"
                                style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                MenuProps={{
                                  PaperProps: { style: { maxHeight: 350 } }
                                }}
                              >
                                {/* User's local timezone (auto-detected from laptop) */}
                                <MenuItem
                                  key="local-tz"
                                  value={LOCAL_TIMEZONE}
                                  style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: 600 }}
                                >
                                  🖥 Local — {LOCAL_TIMEZONE}
                                </MenuItem>
                                <MenuItem
                                  key="utc-tz"
                                  value="UTC"
                                  style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                >
                                  UTC
                                </MenuItem>

                                {/* Country timezones */}
                                <MenuItem disabled style={{ fontFamily: 'Maersk Text', fontSize: '12px', color: '#999', fontWeight: 600 }}>
                                  — Country Timezones —
                                </MenuItem>
                                {Object.entries(COUNTRY_TIMEZONE_MAP)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([country, tz]) => (
                                    <MenuItem
                                      key={`c-${country}`}
                                      value={tz}
                                      style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                    >
                                      {country} ({tz})
                                    </MenuItem>
                                  ))
                                }
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              placeholder="Search cases..."
                              value={statusTableSearchText}
                              onChange={(e) => setStatusTableSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: <SearchIcon style={{ color: '#999', fontSize: '18px', marginRight: '6px' }} />,
                                style: { fontFamily: 'Maersk Text', fontSize: '13px', borderRadius: '8px' }
                              }}
                              style={{ width: 260 }}
                            />
                              <McButton
                                label="Export"
                                appearance="secondary"
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  background: 'white', cursor: 'pointer', fontFamily: 'Maersk Text',
                                  fontSize: '12px', fontWeight: '500', color: '#333',
                                }}
                                click={() => {
                                  let rows = filteredStatusData;
                                  if (statusTableSearchText.trim()) {
                                    const s = statusTableSearchText.trim().toLowerCase();
                                    rows = rows.filter(r => Object.values(r).some(v => v != null && String(v).toLowerCase().includes(s)));
                                  }
                                  handleExportDetailedCases(rows, `${selectedCaseStatus}_Cases_${selectedCountry}`, filteredColumns);
                                }}
                              />
                            <McButton
                              label="← Back to Chart"
                              appearance="secondary"
                              click={handleBackFromStatusDetails}
                            />
                          </div>
                        </div>

                        {/* Status-Specific Detailed Table */}
                        <div style={{ height: '474px', width: '100%' }}>
                          <DataGrid
                            rows={
                              (() => {
                                let baseRows = filteredStatusData;
                                if (statusTableSearchText.trim()) {
                                  const searchLower = statusTableSearchText.trim().toLowerCase();
                                  baseRows = baseRows.filter(row =>
                                    Object.values(row).some(val =>
                                      val != null && String(val).toLowerCase().includes(searchLower)
                                    )
                                  );
                                }
                                return baseRows.map((row, index) => ({ ...row, id: index + 1 }));
                              })()
                            }
                            columns={filteredColumns}
                            pageSize={25}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            disableSelectionOnClick
                            disableRowSelectionOnClick
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'date of the request/case', sort: 'desc' }],
                              },
                            }}
                            sx={{
                              fontFamily: "Maersk Text",
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#ededed",
                              },
                              "& .MuiDataGrid-virtualScrollerContent": {
                                backgroundColor: "white",
                              },
                              "& .MuiDataGrid-cell": {
                                fontSize: "12px",
                              },
                              "& .MuiDataGrid-columnHeader": {
                                fontSize: "12px",
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Detailed Table Display - Shows when drilling down into a year or country */}
                {showDetailedTable && (
                  <div style={{ marginTop: "0" }}>
                    {/* Loading indicator for country-specific API call */}
                    {loadingCountryData && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '200px',
                        flexDirection: 'column' 
                      }}>
                        <McLoadingIndicator
                          appearance="primary"
                          style={{ fontSize: "24px", transform: "scale(1.5)", marginBottom: "16px" }}
                        />
                        <span style={{ color: "#6A6A6A", fontSize: "14px" }}>
                          Loading detailed cases for {selectedCountryForDetails}...
                        </span>
                      </div>
                    )}

                    {!loadingCountryData && (
                      <>
                        {/* Table Header with Navigation */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '20px',
                          padding: '2px 0',
                          flexWrap: 'wrap',
                          gap: '12px'
                        }}>
                          <h3 style={{ 
                            margin: '0', 
                            fontSize: '18px', 
                            fontWeight: '600',
                            color: '#333'
                          }}>
                            {selectedCountryForDetails 
                              ? `Detailed Cases for ${selectedCountryForDetails}` 
                              : `Detailed Cases for Year ${selectedYear}`
                            }
                          </h3>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            {/* Purpose Dropdown */}
                            <FormControl variant="standard" style={{ minWidth: 180 }}>
                              <InputLabel
                                id="purpose-select-label-detail"
                                style={{ fontFamily: 'Maersk Text', fontSize: '14px' }}
                              >
                                Purpose
                              </InputLabel>
                              <Select
                                labelId="purpose-select-label-detail"
                                id="purpose-select-detail"
                                value={selectedPurpose}
                                onChange={(e) => setSelectedPurpose(e.target.value)}
                                label="Purpose"
                                style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                              >
                                <MenuItem value="All" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>All</MenuItem>
                                <MenuItem value="SLA" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>SLA</MenuItem>
                                <MenuItem value="Cashapp" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Cashapp</MenuItem>
                                <MenuItem value="Cashapp/SLA" style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}>Cashapp/SLA</MenuItem>
                              </Select>
                            </FormControl>

                            {/* Timezone Dropdown (Display Timezone) - now shown for country drilldown */}
                            <FormControl variant="standard" style={{ minWidth: 260 }}>
                              <InputLabel
                                id="tz-select-label-detail"
                                style={{ fontFamily: 'Maersk Text', fontSize: '14px' }}
                              >
                                Display Timezone
                              </InputLabel>
                              <Select
                                labelId="tz-select-label-detail"
                                id="tz-select-detail"
                                value={selectedTimezone}
                                onChange={(e) => setSelectedTimezone(e.target.value)}
                                label="Display Timezone"
                                style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                MenuProps={{
                                  PaperProps: { style: { maxHeight: 350 } }
                                }}
                              >
                                {/* User's local timezone (auto-detected from laptop) */}
                                <MenuItem
                                  key="local-tz"
                                  value={LOCAL_TIMEZONE}
                                  style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: 600 }}
                                >
                                  🖥 Local — {LOCAL_TIMEZONE}
                                </MenuItem>
                                <MenuItem
                                  key="utc-tz"
                                  value="UTC"
                                  style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                >
                                  UTC
                                </MenuItem>

                                {/* Country timezones */}
                                <MenuItem disabled style={{ fontFamily: 'Maersk Text', fontSize: '12px', color: '#999', fontWeight: 600 }}>
                                  — Country Timezones —
                                </MenuItem>
                                {Object.entries(COUNTRY_TIMEZONE_MAP)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([country, tz]) => (
                                    <MenuItem
                                      key={`c-${country}`}
                                      value={tz}
                                      style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                    >
                                      {country} ({tz})
                                    </MenuItem>
                                  ))
                                }
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              placeholder="Search cases..."
                              value={detailedTableSearchText}
                              onChange={(e) => setDetailedTableSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: <SearchIcon style={{ color: '#999', fontSize: '18px', marginRight: '6px' }} />,
                                style: { fontFamily: 'Maersk Text', fontSize: '13px', borderRadius: '8px' }
                              }}
                              style={{ width: 260 }}
                            />
                            <McButton
                              label="Export"
                              appearance="secondary"
                              click={() => {
                                let rows = selectedCountryForDetails ? filteredCountryData : detailRows;
                                if (detailedTableSearchText.trim()) {
                                  const s = detailedTableSearchText.trim().toLowerCase();
                                  rows = rows.filter(r => Object.values(r).some(v => v != null && String(v).toLowerCase().includes(s)));
                                }
                                const label = selectedCountryForDetails ? `Detailed_Cases_${selectedCountryForDetails}` : `Detailed_Cases_Year_${selectedYear}`;
                                handleExportDetailedCases(rows, label, filteredColumns);
                              }}
                            />
                            <McButton
                              label="← Back to Summary"
                              appearance="secondary"
                              click={handleBackToSummary}
                            />
                          </div>
                        </div>

                        {/* Detailed Table */}
                        <div style={{ height: '474px', width: '100%' }}>
                          <DataGrid
                            rows={
                              (() => {
                                let baseRows = selectedCountryForDetails 
                                  ? filteredCountryData
                                  : detailRows;
                                // Apply search filter
                                if (detailedTableSearchText.trim()) {
                                  const searchLower = detailedTableSearchText.trim().toLowerCase();
                                  baseRows = baseRows.filter(row =>
                                    Object.values(row).some(val =>
                                      val != null && String(val).toLowerCase().includes(searchLower)
                                    )
                                  );
                                }
                                return baseRows.map((row, index) => ({ ...row, id: index + 1 }));
                              })()
                            }
                            columns={filteredColumns}
                            pageSize={25}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            disableSelectionOnClick
                            disableRowSelectionOnClick
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'date of the request/case', sort: 'desc' }],
                              },
                            }}
                            sx={{
                              fontFamily: "Maersk Text",
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#ededed",
                              },
                              "& .MuiDataGrid-virtualScrollerContent": {
                                backgroundColor: "white",
                              },
                              "& .MuiDataGrid-cell": {
                                fontSize: "12px",
                              },
                              "& .MuiDataGrid-columnHeader": {
                                fontSize: "12px",
                              },
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 2 && (
              // Productivity Report Tab Content
              <div>
                {/* Loading State */}
                {loadingProductivityData ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '400px',
                    flexDirection: 'column' 
                  }}>
                    <McLoadingIndicator
                      appearance="primary"
                      style={{ fontSize: "36px", transform: "scale(2)", marginBottom: "16px" }}
                    />
                    <span style={{ color: "#6A6A6A", fontSize: "16px" }}>
                      Loading Productivity Report...
                    </span>
                    <span style={{ color: "#999", fontSize: "12px", marginTop: "8px" }}>
                      Fetching and processing case data
                    </span>
                  </div>
                ) : (
                  <>
                    {/* Productivity Report Header with Controls */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <h3 style={{ 
                        margin: '0', 
                        fontSize: '18px', 
                        fontWeight: '600',
                        color: '#333'
                      }}>
                        Productivity Report ({productivityTotalCases} rows)
                        {selectedTrendsCountry.length > 0 && (
                          <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '400',
                            color: '#1686BD',
                            marginLeft: '10px'
                          }}>
                            — {selectedTrendsCountry.join(', ')}
                          </span>
                        )}
                      </h3>

                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {/* Timezone Dropdown */}
                        <FormControl variant="standard" style={{ minWidth: 260 }}>
                          <InputLabel
                            id="tz-select-productivity"
                            style={{ fontFamily: 'Maersk Text', fontSize: '14px' }}
                          >
                            Display Timezone
                          </InputLabel>
                          <Select
                            labelId="tz-select-productivity"
                            id="tz-select-productivity-id"
                            value={selectedTimezone}
                            onChange={(e) => setSelectedTimezone(e.target.value)}
                            label="Display Timezone"
                            style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                            MenuProps={{
                              PaperProps: { style: { maxHeight: 350 } }
                            }}
                          >
                            {/* User's local timezone (auto-detected from laptop) */}
                            <MenuItem
                              key="local-tz-prod"
                              value={LOCAL_TIMEZONE}
                              style={{ fontFamily: 'Maersk Text', fontSize: '13px', fontWeight: 600 }}
                            >
                              🖥 Local — {LOCAL_TIMEZONE}
                            </MenuItem>
                            <MenuItem
                              key="utc-tz-prod"
                              value="UTC"
                              style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                            >
                              UTC
                            </MenuItem>

                            {/* Country timezones */}
                            <MenuItem disabled style={{ fontFamily: 'Maersk Text', fontSize: '12px', color: '#999', fontWeight: 600 }}>
                              — Country Timezones —
                            </MenuItem>
                            {Object.entries(COUNTRY_TIMEZONE_MAP)
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([country, tz]) => (
                                <MenuItem
                                  key={`prod-c-${country}`}
                                  value={tz}
                                  style={{ fontFamily: 'Maersk Text', fontSize: '13px' }}
                                >
                                  {country} ({tz})
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>

                        {/* Search Field */}
                        <TextField
                          size="small"
                          placeholder="Search productivity data..."
                          value={productivityTableSearchText}
                          onChange={(e) => setProductivityTableSearchText(e.target.value)}
                          InputProps={{
                            startAdornment: <SearchIcon style={{ color: '#999', fontSize: '18px', marginRight: '6px' }} />,
                            style: { fontFamily: 'Maersk Text', fontSize: '13px', borderRadius: '8px' }
                          }}
                          style={{ width: 290 }}
                        />

                        {/* Export Button */}
                        <McButton
                          label={exportingProductivity ? "Exporting..." : "Export All"}
                          appearance="secondary"
                          disabled={exportingProductivity}
                          click={() => handleExportProductivityReport()}
                        />
                      </div>
                    </div>

                    {/* Export progress indicator */}
                    {exportingProductivity && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 16px',
                        marginBottom: '12px',
                        backgroundColor: '#E2F3FB',
                        borderRadius: '8px',
                        border: '1px solid #A1D1E8'
                      }}>
                        <McLoadingIndicator
                          appearance="primary"
                          style={{ fontSize: "16px" }}
                        />
                        <span style={{ color: '#003E62', fontSize: '13px', fontWeight: 500 }}>
                          Exporting all records for {selectedTrendsCountry.length > 0 ? selectedTrendsCountry.join(', ') : 'all countries'}... Please wait.
                        </span>
                      </div>
                    )}

                    {/* Productivity Report Table */}
                    {filteredProductivityData.length > 0 ? (
                      <div style={{ height: '600px', width: '100%' }}>
                        <DataGrid
                          rows={
                            (() => {
                              let baseRows = filteredProductivityData;
                              // Apply client-side search filter
                              if (productivityTableSearchText.trim()) {
                                const searchLower = productivityTableSearchText.trim().toLowerCase();
                                baseRows = baseRows.filter(row =>
                                  Object.values(row).some(val =>
                                    val != null && String(val).toLowerCase().includes(searchLower)
                                  )
                                );
                              }
                              return baseRows;
                            })()
                          }
                          columns={productivityReportColumns}
                          getRowId={(row) => row.id ?? row['DA Case Number #']}
                          pagination
                          paginationMode="server"
                          rowCount={productivityTotal}
                          paginationModel={{ page: productivityPage, pageSize: productivityPageSize }}
                          onPaginationModelChange={(newModel) => {
                            if (newModel.pageSize !== productivityPageSize) {
                              setProductivityPageSize(newModel.pageSize);
                              setProductivityPage(0);
                            } else {
                              setProductivityPage(newModel.page);
                            }
                          }}
                          pageSizeOptions={[25, 50, 100]}
                          loading={loadingProductivityData}
                          sortingMode="server"
                          sortModel={productivitySortModel}
                          onSortModelChange={(model) => {
                            setProductivitySortModel(model);
                            setProductivityPage(0);
                          }}
                          disableSelectionOnClick
                          disableRowSelectionOnClick
                          sx={{
                            fontFamily: "Maersk Text",
                            "& .MuiDataGrid-columnHeaders": {
                              backgroundColor: "#ededed",
                              fontWeight: 700,
                            },
                            "& .MuiDataGrid-virtualScrollerContent": {
                              backgroundColor: "white",
                            },
                            "& .MuiDataGrid-cell": {
                              fontSize: "12px",
                            },
                            "& .MuiDataGrid-columnHeader": {
                              fontSize: "12px",
                            },
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#6c757d',
                        fontSize: '14px',
                        flexDirection: 'column',
                        minHeight: '400px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '2px dashed #dee2e6',
                      }}>
                        {selectedTrendsCountry.length === 0 ? (
                          <>
                            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🌍</div>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#333' }}>Please select a country filter to see the results</p>
                            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#999' }}>Use the "Select Country" dropdown above to filter productivity data</p>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                            <p style={{ margin: 0 }}>No data available for the selected filters.</p>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating Metrics Chatbot [metrics-chatbot] commented out */}
      {/**
       * <MetricsChatbot metricsData={countryAgg} processedData={summary || {}} />
       */}

      {/* ── Trends Drilldown Modal: per-day breakdown for a week ── */}
      <Dialog
        open={trendsDrilldownOpen}
        onClose={() => setTrendsDrilldownOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Maersk Text',
            //maxHeight: '100vh',
            maxWidth: '280vh',
            height: '80%',
          }
        }}
      >
        <DialogTitle style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'Maersk Text',
          borderBottom: '1px solid #e0e0e0',
          padding: '16px 24px'
        }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: '18px', color: '#333' }}>
              Daily Breakdown — {trendsDrilldownStatus}
            </span>
            <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
              Week: {trendsDrilldownWeekLabel} &nbsp;|&nbsp; Countries: {selectedCountryLabel || 'All'}
            </div>
          </div>
          <IconButton onClick={() => setTrendsDrilldownOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '24px', overflowY: 'auto' }}>
          {(() => {
            const countries = Object.keys(trendsDrilldownDailyData);
            if (countries.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No data available for this week.
                </div>
              );
            }

            // Assign a distinct color to each country
            const COUNTRY_BAR_COLORS = [
              '#003E62', '#1686BD', '#A1D1E8', '#6A6A6A', '#119C85',
              '#034E45', '#97DECD', '#6CA926', '#316307', '#903E00',
              '#B9E371'
            ];
            const countryColorMap = {};
            countries.forEach((c, i) => {
              countryColorMap[c] = COUNTRY_BAR_COLORS[i % COUNTRY_BAR_COLORS.length];
            });

            // Merge per-country data into grouped rows: [{ dayLabel, date, countryA: n, countryB: n, ... }]
            const firstCountryData = trendsDrilldownDailyData[countries[0]] || [];
            const groupedData = firstCountryData.map((dayEntry, dayIdx) => {
              const row = { dayLabel: dayEntry.dayLabel, date: dayEntry.date };
              countries.forEach(c => {
                const cd = trendsDrilldownDailyData[c];
                row[c] = cd && cd[dayIdx] ? cd[dayIdx].cases : 0;
              });
              return row;
            });

            // Compute summary across all countries
            const grandTotal = countries.reduce((sum, c) =>
              sum + (trendsDrilldownDailyData[c] || []).reduce((s, d) => s + d.cases, 0), 0);
            // Peak day across all countries combined
            const dayTotals = groupedData.map(row => ({
              dayLabel: row.dayLabel,
              total: countries.reduce((s, c) => s + (row[c] || 0), 0)
            }));
            const peakDay = dayTotals.reduce((max, d) => d.total > max.total ? d : max, { total: 0, dayLabel: '—' });
            const daysCount = groupedData.length || 1;

            return (
              <>
                {/* Summary cards */}
                <div style={{
                  display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap'
                }}>
                  <div style={{
                    background: '#f0f8ff', borderRadius: '10px', padding: '14px 24px',
                    flex: '1', minWidth: '140px', textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Cases</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1686BD' }}>{grandTotal}</div>
                  </div>
                  <div style={{
                    background: '#f0fff4', borderRadius: '10px', padding: '14px 24px',
                    flex: '1', minWidth: '140px', textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Peak Day</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#6CA926' }}>
                      {`${peakDay.dayLabel} (${peakDay.total})`}
                    </div>
                  </div>
                  <div style={{
                    background: '#fff8f0', borderRadius: '10px', padding: '14px 24px',
                    flex: '1', minWidth: '140px', textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Daily Avg</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#F3870C' }}>
                      {(grandTotal / daysCount).toFixed(1)}
                    </div>
                  </div>
                </div>

                {/* Per-country breakdown pills */}
                {countries.length > 1 && (
                  <div style={{
                    display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap'
                  }}>
                    {countries.map(c => {
                      const countryTotal = (trendsDrilldownDailyData[c] || []).reduce((s, d) => s + d.cases, 0);
                      return (
                        <div key={c} style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          background: '#f5f5f5', borderRadius: '8px', padding: '8px 14px',
                          fontSize: '13px', fontFamily: 'Maersk Text'
                        }}>
                          <span style={{
                            width: '10px', height: '10px', borderRadius: '2px',
                            backgroundColor: countryColorMap[c], display: 'inline-block'
                          }} />
                          <span style={{ fontWeight: 600, color: '#333' }}>{c}</span>
                          <span style={{ color: '#666' }}>({countryTotal})</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Trends Line Chart — one line per country per day */}
                <ResponsiveContainer width="100%" height={380}>
                  <AreaChart
                    data={groupedData}
                    margin={{ top: 10, right: 20, left: 10, bottom: 40 }}
                  >
                    <defs>
                      {countries.map(c => (
                        <linearGradient key={`grad-${c}`} id={`gradient-${c.replace(/\s+/g, '_')}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={countryColorMap[c]} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={countryColorMap[c]} stopOpacity={0.02} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="dayLabel"
                      tick={{ fontSize: 12, fontFamily: 'Maersk Text' }}
                      angle={-30}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Number of Cases', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    />
                    <RechartsTooltip
                      contentStyle={{ fontFamily: 'Maersk Text', fontSize: '13px', borderRadius: '8px' }}
                    />
                    <Legend
                      wrapperStyle={{ fontFamily: 'Maersk Text', fontSize: '12px', paddingTop: '8px' }}
                    />
                    {countries.map(c => (
                      <Area
                        key={c}
                        type="monotone"
                        dataKey={c}
                        name={c}
                        stroke={countryColorMap[c]}
                        strokeWidth={2.5}
                        fill={`url(#gradient-${c.replace(/\s+/g, '_')})`}
                        dot={{ r: 4, fill: countryColorMap[c], strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 6, fill: countryColorMap[c], strokeWidth: 2, stroke: '#fff' }}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetricsSnapshot;